/* DNA Auto — voice smart search ("DNA Assistant"), Sept 2026.
   Tap the mic in the search pill (or type a sentence with a vehicle in it) and say, in plain words, ONE vehicle and
   the parts you need: "parts for my 2015 dodge ram 1500, find me an alternator and brake pads". The assistant
   works out the vehicle (year / make / model) and the parts, asks the follow-ups a parts counter would ask (which
   cab? which engine?) as tap bubbles, then shows every matching part we carry, ranked by whether its listing names
   that vehicle and those years. Everything runs in the browser: Web Speech API for listening (iOS 14.5+ Safari,
   Android Chrome), the store's own search (/search?view=dna-json) for products, no outside services, nothing
   stored except the vehicle (localStorage dna_vehicle) so the next visit can skip straight to the parts. */
(function () {
  'use strict';

  // ======================================================================= vehicle knowledge
  // Makes: canonical name, spoken aliases (longest first when scanning), regex used on product text.
  var MAKES = [
    ['Ram', ['dodge ram', 'ram'], '(?:ram|dodge)'],
    ['Dodge', ['dodge'], 'dodge'],
    ['Ford', ['ford'], 'ford'],
    ['Chevrolet', ['chevrolet', 'chevy', 'chev'], '(?:chevrolet|chevy|chev)'],
    ['GMC', ['gmc'], 'gmc'],
    ['Toyota', ['toyota'], 'toyota'],
    ['Honda', ['honda'], 'honda'],
    ['Nissan', ['nissan'], 'nissan'],
    ['Jeep', ['jeep'], 'jeep'],
    ['Subaru', ['subaru'], 'subaru'],
    ['Hyundai', ['hyundai'], 'hyundai'],
    ['Kia', ['kia'], 'kia'],
    ['Mazda', ['mazda'], 'mazda'],
    ['Lexus', ['lexus'], 'lexus'],
    ['Acura', ['acura'], 'acura'],
    ['Infiniti', ['infiniti', 'infinity'], 'infiniti'],
    ['BMW', ['bmw'], 'bmw'],
    ['Mercedes-Benz', ['mercedes benz', 'mercedes-benz', 'mercedes', 'benz'], '(?:mercedes|benz)'],
    ['Audi', ['audi'], 'audi'],
    ['Volkswagen', ['volkswagen', 'vw'], '(?:volkswagen|vw)'],
    ['Volvo', ['volvo'], 'volvo'],
    ['Porsche', ['porsche'], 'porsche'],
    ['Tesla', ['tesla'], 'tesla'],
    ['Buick', ['buick'], 'buick'],
    ['Cadillac', ['cadillac'], 'cadillac'],
    ['Lincoln', ['lincoln'], 'lincoln'],
    ['Chrysler', ['chrysler'], 'chrysler'],
    ['Mitsubishi', ['mitsubishi'], 'mitsubishi'],
    ['Land Rover', ['land rover', 'range rover'], '(?:land\\s*rover|range\\s*rover)'],
    ['Mini', ['mini cooper', 'mini countryman', 'mini clubman'], 'mini\\s*cooper'],
    ['Genesis', ['genesis'], 'genesis'],
    ['Rivian', ['rivian'], 'rivian'],
    ['Pontiac', ['pontiac'], 'pontiac'],
    ['Saturn', ['saturn'], 'saturn'],
    ['Hummer', ['hummer'], 'hummer'],
    ['Scion', ['scion'], 'scion'],
    ['Isuzu', ['isuzu'], 'isuzu'],
    ['Suzuki', ['suzuki'], 'suzuki'],
    ['Fiat', ['fiat'], 'fiat'],
    ['Jaguar', ['jaguar'], 'jaguar'],
    ['Alfa Romeo', ['alfa romeo', 'alfa'], 'alfa\\s*romeo'],
    ['Oldsmobile', ['oldsmobile', 'olds'], 'oldsmobile'],
    ['Mercury', ['mercury'], 'mercury'],
    ['Plymouth', ['plymouth'], 'plymouth'],
    ['Saab', ['saab'], 'saab']
  ];

  // Engine sets by generation (reused below).
  var E = {
    ram1500new: ['3.6L V6', '5.7L HEMI V8', '3.0L EcoDiesel'],
    ram1500old: ['3.7L V6', '4.7L V8', '5.7L HEMI V8'],
    ramHDnew: ['6.4L HEMI V8', '6.7L Cummins Diesel'],
    ramHDmid: ['5.7L HEMI V8', '6.4L HEMI V8', '6.7L Cummins Diesel'],
    ramHDold: ['5.7L HEMI V8', '5.9L / 6.7L Cummins Diesel'],
    f150_21: ['2.7L EcoBoost V6', '3.5L EcoBoost V6', '5.0L V8', '3.3L V6', '3.5L PowerBoost Hybrid'],
    f150_18: ['2.7L EcoBoost V6', '3.5L EcoBoost V6', '5.0L V8', '3.3L V6', '3.0L Power Stroke Diesel'],
    f150_15: ['2.7L EcoBoost V6', '3.5L EcoBoost V6', '5.0L V8', '3.5L V6'],
    f150_11: ['3.7L V6', '5.0L V8', '3.5L EcoBoost V6', '6.2L V8'],
    f150_04: ['4.2L V6', '4.6L V8', '5.4L V8'],
    sd17: ['6.2L V8', '7.3L V8', '6.7L Power Stroke Diesel'],
    sd11: ['6.2L V8', '6.7L Power Stroke Diesel'],
    sd08: ['5.4L V8', '6.8L V10', '6.4L Power Stroke Diesel'],
    sd99: ['5.4L V8', '6.8L V10', '7.3L / 6.0L Power Stroke Diesel'],
    gm1500_19: ['2.7L Turbo I4', '4.3L V6', '5.3L V8', '6.2L V8', '3.0L Duramax Diesel'],
    gm1500_14: ['4.3L V6', '5.3L V8', '6.2L V8'],
    gm1500_07: ['4.3L V6', '4.8L V8', '5.3L V8', '6.0L / 6.2L V8'],
    gm1500_99: ['4.3L V6', '4.8L V8', '5.3L V8', '6.0L V8'],
    gmHD20: ['6.6L V8 gas', '6.6L Duramax Diesel'],
    gmHD11: ['6.0L V8', '6.6L Duramax Diesel'],
    gmHD01: ['6.0L V8', '8.1L V8', '6.6L Duramax Diesel'],
    gmSUV21: ['5.3L V8', '6.2L V8', '3.0L Duramax Diesel'],
    gmSUV15: ['5.3L V8', '6.2L V8'],
    gmSUV07: ['5.3L V8', '6.0L V8', '6.2L V8'],
    gmMid23: ['2.7L Turbo I4'],
    gmMid15: ['2.5L I4', '3.6L V6', '2.8L Duramax Diesel'],
    gmMid04: ['2.8L / 2.9L I4', '3.5L / 3.7L I5', '5.3L V8'],
    tacoma24: ['2.4L Turbo I4', '2.4L Hybrid'],
    tacoma16: ['2.7L I4', '3.5L V6'],
    tacoma05: ['2.7L I4', '4.0L V6'],
    tundra22: ['3.4L Twin-Turbo V6', 'i-FORCE MAX Hybrid'],
    tundra07: ['4.0L V6', '4.6L V8', '5.7L V8'],
    tundra00: ['3.4L / 4.0L V6', '4.7L V8'],
    jk: ['3.6L V6'], jk07: ['3.8L V6'], tj: ['2.4L I4', '4.0L I6'],
    jl: ['3.6L V6', '2.0L Turbo', '3.0L EcoDiesel', '4xe Hybrid', '6.4L V8 (392)'],
    gc22: ['3.6L V6', '5.7L V8', '4xe Hybrid'],
    gc11: ['3.6L V6', '5.7L V8', '3.0L EcoDiesel', '6.4L V8 SRT'],
    gc05: ['3.7L V6', '4.7L V8', '5.7L V8'],
    frontier20: ['3.8L V6'], frontier05: ['2.5L I4', '4.0L V6'],
    titan: ['5.6L V8', '5.0L Cummins Diesel (XD)'],
    civic16: ['2.0L I4', '1.5L Turbo'], civic12: ['1.8L I4', '2.4L I4 (Si)'], civic06: ['1.8L I4', '2.0L I4 (Si)'],
    accord18: ['1.5L Turbo', '2.0L Turbo', 'Hybrid'], accord13: ['2.4L I4', '3.5L V6'], accord08: ['2.4L I4', '3.5L V6'],
    crv17: ['1.5L Turbo', '2.4L I4', 'Hybrid'], crv12: ['2.4L I4'],
    camry18: ['2.5L I4', '3.5L V6', 'Hybrid'], camry12: ['2.5L I4', '3.5L V6', 'Hybrid'],
    corolla20: ['1.8L I4', '2.0L I4', 'Hybrid'], corolla14: ['1.8L I4'],
    rav4_19: ['2.5L I4', 'Hybrid', 'Prime Plug-in'], rav4_13: ['2.5L I4', 'Hybrid (16-18)'],
    fourrunner: ['4.0L V6'], fourrunner03: ['4.0L V6', '4.7L V8'],
    highlander: ['2.4L Turbo (23+)', '3.5L V6', 'Hybrid'],
    explorer20: ['2.3L EcoBoost I4', '3.0L EcoBoost V6', '3.3L Hybrid'], explorer11: ['2.0L / 2.3L EcoBoost I4', '3.5L V6', '3.5L EcoBoost V6'], explorer06: ['4.0L V6', '4.6L V8'],
    escape20: ['1.5L EcoBoost', '2.0L EcoBoost', '2.5L Hybrid'], escape13: ['1.5L / 1.6L EcoBoost', '2.0L EcoBoost', '2.5L I4'], escape08: ['2.5L I4', '3.0L V6', 'Hybrid'],
    mustang15: ['2.3L EcoBoost', '5.0L V8', '3.7L V6 (15-17)'], mustang11: ['3.7L V6', '5.0L V8'], mustang05: ['4.0L V6', '4.6L V8'],
    bronco: ['2.3L EcoBoost I4', '2.7L EcoBoost V6'],
    ranger19: ['2.3L EcoBoost I4', '2.7L EcoBoost V6 (24+)'], ranger98: ['2.3L I4', '3.0L V6', '4.0L V6'],
    expedition18: ['3.5L EcoBoost V6'], expedition07: ['5.4L V8', '3.5L EcoBoost V6 (15-17)'],
    charger: ['3.6L V6', '5.7L HEMI V8', '6.4L HEMI V8', '6.2L Supercharged HEMI'],
    durango: ['3.6L V6', '5.7L HEMI V8', '6.4L HEMI V8'],
    caravan11: ['3.6L V6'], caravan08: ['3.3L / 3.8L V6'],
    dakota: ['3.7L V6', '4.7L V8'],
    camaro: ['2.0L Turbo', '3.6L V6', '6.2L V8'],
    equinox: ['1.5L Turbo', '2.0L Turbo', '2.4L I4 (10-17)'],
    traverse: ['3.6L V6', '2.5L Turbo (24+)'],
    subaru: ['2.5L Boxer', '2.4L Turbo Boxer', '2.0L Boxer'],
    hyundaiKia: ['2.0L I4', '2.5L I4', '1.6L Turbo', '2.5L Turbo', 'Hybrid'],
    telluride: ['3.8L V6'],
    altima: ['2.5L I4', '2.0L VC-Turbo', '3.5L V6 (13-18)'],
    rogue: ['2.5L I4', '1.5L VC-Turbo (22+)'],
    pathfinder: ['3.5L V6'],
    gladiator: ['3.6L V6', '3.0L EcoDiesel'],
    cherokee: ['2.4L I4', '3.2L V6', '2.0L Turbo'],
    escalade: ['6.2L V8', '3.0L Duramax Diesel'],
    navigator: ['3.5L EcoBoost V6'],
    pacifica: ['3.6L V6', 'Plug-in Hybrid'],
    chrysler300: ['3.6L V6', '5.7L HEMI V8'],
    lexusRX: ['3.5L V6', '2.4L Turbo (23+)', 'Hybrid'],
    bmw3: ['2.0L Turbo I4', '3.0L Turbo I6'],
    mbC: ['2.0L Turbo I4', '3.0L V6'],
    mazda: ['2.5L I4', '2.5L Turbo', '2.0L I4'],
    vw: ['1.4L / 1.5L Turbo', '2.0L Turbo'],
    silverado_cabs: ['Regular Cab', 'Double / Extended Cab', 'Crew Cab']
  };
  // Models: make, display name, spoken aliases, type (truck / suv / car / van), product-text regex (optional),
  // body options (cabs or doors or bodies), engines by year range [[from, to, list], ...]
  var MODELS = [
    ['Ram', '1500', ['ram 1500', '1500', 'ram fifteen hundred', 'fifteen hundred'], 'truck', '(?:\\bram\\b|\\bdodge\\b)[^.;|]{0,30}\\b1500\\b|\\b1500\\b[^.;|]{0,10}\\bram\\b', ['Regular Cab', 'Quad Cab', 'Crew Cab'], [[2019, 2030, E.ram1500new], [2013, 2018, E.ram1500new], [2009, 2012, E.ram1500old], [2002, 2008, ['3.7L V6', '4.7L V8', '5.7L HEMI V8', '5.9L V8']], [1994, 2001, ['3.9L V6', '5.2L V8', '5.9L V8']]]],
    ['Ram', '2500', ['ram 2500', '2500', 'twenty five hundred'], 'truck', '(?:\\bram\\b|\\bdodge\\b)[^.;|]{0,30}\\b2500\\b|\\b2500\\b[^.;|]{0,10}\\bram\\b', ['Regular Cab', 'Crew Cab', 'Mega Cab'], [[2019, 2030, E.ramHDnew], [2014, 2018, E.ramHDmid], [2003, 2013, E.ramHDold]]],
    ['Ram', '3500', ['ram 3500', '3500', 'thirty five hundred'], 'truck', '(?:\\bram\\b|\\bdodge\\b)[^.;|]{0,30}\\b3500\\b|\\b3500\\b[^.;|]{0,10}\\bram\\b', ['Regular Cab', 'Crew Cab', 'Mega Cab'], [[2019, 2030, E.ramHDnew], [2014, 2018, E.ramHDmid], [2003, 2013, E.ramHDold]]],
    ['Ram', 'ProMaster', ['promaster', 'pro master'], 'van', null, null, [[2014, 2030, ['3.6L V6']]]],
    ['Dodge', 'Dakota', ['dakota'], 'truck', null, ['Extended Cab', 'Quad Cab / Crew Cab'], [[2005, 2011, E.dakota], [1997, 2004, ['2.5L I4', '3.9L V6', '4.7L V8', '5.9L V8']]]],
    ['Dodge', 'Durango', ['durango'], 'suv', null, null, [[2011, 2030, E.durango], [2004, 2009, ['3.7L V6', '4.7L V8', '5.7L HEMI V8']]]],
    ['Dodge', 'Charger', ['charger'], 'car', null, null, [[2011, 2030, E.charger], [2006, 2010, ['2.7L V6', '3.5L V6', '5.7L HEMI V8', '6.1L HEMI V8']]]],
    ['Dodge', 'Challenger', ['challenger'], 'car', null, null, [[2011, 2030, E.charger], [2008, 2010, ['3.5L V6', '5.7L HEMI V8', '6.1L HEMI V8']]]],
    ['Dodge', 'Grand Caravan', ['grand caravan', 'caravan'], 'van', null, null, [[2011, 2020, E.caravan11], [2008, 2010, E.caravan08]]],
    ['Dodge', 'Journey', ['journey'], 'suv', null, null, [[2009, 2020, ['2.4L I4', '3.6L V6', '3.5L V6 (09-10)']]]],
    ['Ford', 'F-150', ['f-150', 'f150', 'f 150', 'f one fifty', 'f one-fifty'], 'truck', '\\bf[\\s-]?150\\b', ['Regular Cab', 'SuperCab', 'SuperCrew'], [[2021, 2030, E.f150_21], [2018, 2020, E.f150_18], [2015, 2017, E.f150_15], [2011, 2014, E.f150_11], [2004, 2010, E.f150_04], [1997, 2003, E.f150_04]]],
    ['Ford', 'F-250 Super Duty', ['f-250', 'f250', 'f 250', 'f two fifty', 'super duty'], 'truck', '\\bf[\\s-]?250\\b|\\bsuper\\s*duty\\b', ['Regular Cab', 'SuperCab', 'Crew Cab'], [[2017, 2030, E.sd17], [2011, 2016, E.sd11], [2008, 2010, E.sd08], [1999, 2007, E.sd99]]],
    ['Ford', 'F-350 Super Duty', ['f-350', 'f350', 'f 350', 'f three fifty'], 'truck', '\\bf[\\s-]?350\\b', ['Regular Cab', 'SuperCab', 'Crew Cab'], [[2017, 2030, E.sd17], [2011, 2016, E.sd11], [2008, 2010, E.sd08], [1999, 2007, E.sd99]]],
    ['Ford', 'Ranger', ['ranger'], 'truck', null, ['SuperCab', 'SuperCrew'], [[2019, 2030, E.ranger19], [1998, 2011, E.ranger98]]],
    ['Ford', 'Maverick', ['maverick'], 'truck', null, null, [[2022, 2030, ['2.0L EcoBoost', '2.5L Hybrid']]]],
    ['Ford', 'Explorer', ['explorer'], 'suv', null, null, [[2020, 2030, E.explorer20], [2011, 2019, E.explorer11], [2006, 2010, E.explorer06]]],
    ['Ford', 'Escape', ['escape'], 'suv', null, null, [[2020, 2030, E.escape20], [2013, 2019, E.escape13], [2008, 2012, E.escape08]]],
    ['Ford', 'Expedition', ['expedition'], 'suv', null, null, [[2018, 2030, E.expedition18], [2007, 2017, E.expedition07]]],
    ['Ford', 'Edge', ['edge'], 'suv', null, null, [[2015, 2024, ['2.0L EcoBoost', '2.7L EcoBoost V6', '3.5L V6 (15-18)']], [2007, 2014, ['3.5L V6', '2.0L EcoBoost (11-14)']]]],
    ['Ford', 'Bronco', ['bronco'], 'suv', '\\bbronco\\b(?!\\s*sport)', ['2-door', '4-door'], [[2021, 2030, E.bronco]]],
    ['Ford', 'Bronco Sport', ['bronco sport'], 'suv', null, null, [[2021, 2030, ['1.5L EcoBoost', '2.0L EcoBoost']]]],
    ['Ford', 'Mustang', ['mustang'], 'car', null, ['Coupe', 'Convertible'], [[2015, 2030, E.mustang15], [2011, 2014, E.mustang11], [2005, 2010, E.mustang05]]],
    ['Ford', 'Focus', ['focus'], 'car', null, ['Sedan', 'Hatchback'], [[2012, 2018, ['2.0L I4', '1.0L EcoBoost', '2.3L EcoBoost (RS)']]]],
    ['Ford', 'Fusion', ['fusion'], 'car', null, null, [[2013, 2020, ['2.5L I4', '1.5L EcoBoost', '2.0L EcoBoost', 'Hybrid']]]],
    ['Ford', 'Transit', ['transit'], 'van', null, null, [[2015, 2030, ['3.5L V6', '3.5L EcoBoost V6', '3.7L V6 (15-19)']]]],
    ['Chevrolet', 'Silverado 1500', ['silverado 1500', 'silverado', 'silverado fifteen hundred'], 'truck', '\\bsilverado\\b', E.silverado_cabs, [[2019, 2030, E.gm1500_19], [2014, 2018, E.gm1500_14], [2007, 2013, E.gm1500_07], [1999, 2006, E.gm1500_99]]],
    ['Chevrolet', 'Silverado 2500 / 3500 HD', ['silverado 2500', 'silverado 3500', 'silverado hd', '2500hd', '3500hd'], 'truck', '\\bsilverado\\b[^.;|]{0,14}\\b(?:2500|3500|hd)\\b', E.silverado_cabs, [[2020, 2030, E.gmHD20], [2011, 2019, E.gmHD11], [2001, 2010, E.gmHD01]]],
    ['Chevrolet', 'Colorado', ['colorado'], 'truck', null, ['Extended Cab', 'Crew Cab'], [[2023, 2030, E.gmMid23], [2015, 2022, E.gmMid15], [2004, 2012, E.gmMid04]]],
    ['Chevrolet', 'Tahoe', ['tahoe'], 'suv', null, null, [[2021, 2030, E.gmSUV21], [2015, 2020, E.gmSUV15], [2007, 2014, E.gmSUV07]]],
    ['Chevrolet', 'Suburban', ['suburban'], 'suv', null, null, [[2021, 2030, E.gmSUV21], [2015, 2020, E.gmSUV15], [2007, 2014, E.gmSUV07]]],
    ['Chevrolet', 'Equinox', ['equinox'], 'suv', null, null, [[2010, 2030, E.equinox]]],
    ['Chevrolet', 'Traverse', ['traverse'], 'suv', null, null, [[2009, 2030, E.traverse]]],
    ['Chevrolet', 'Blazer', ['blazer'], 'suv', null, null, [[2019, 2030, ['2.0L Turbo', '3.6L V6', '2.5L I4 (19-21)']]]],
    ['Chevrolet', 'Malibu', ['malibu'], 'car', null, null, [[2016, 2025, ['1.5L Turbo', '2.0L Turbo', 'Hybrid']], [2004, 2015, ['2.4L I4', '2.5L I4', '3.5L / 3.6L V6']]]],
    ['Chevrolet', 'Cruze', ['cruze', 'cruise'], 'car', null, null, [[2011, 2019, ['1.4L Turbo', '1.8L I4', '1.6L Diesel']]]],
    ['Chevrolet', 'Camaro', ['camaro'], 'car', null, ['Coupe', 'Convertible'], [[2016, 2024, E.camaro], [2010, 2015, ['3.6L V6', '6.2L V8']]]],
    ['Chevrolet', 'Corvette', ['corvette'], 'car', null, ['Coupe', 'Convertible'], [[2014, 2030, ['6.2L V8', '5.5L V8 (Z06)']]]],
    ['Chevrolet', 'Express', ['express van', 'express'], 'van', null, null, [[2003, 2030, ['4.3L V6', '6.0L V8', '6.6L V8', '2.8L Duramax Diesel']]]],
    ['GMC', 'Sierra 1500', ['sierra 1500', 'sierra'], 'truck', '\\bsierra\\b(?![^.;|]{0,14}\\b(?:2500|3500|hd)\\b)', E.silverado_cabs, [[2019, 2030, E.gm1500_19], [2014, 2018, E.gm1500_14], [2007, 2013, E.gm1500_07], [1999, 2006, E.gm1500_99]]],
    ['GMC', 'Sierra 2500 / 3500 HD', ['sierra 2500', 'sierra 3500', 'sierra hd'], 'truck', '\\bsierra\\b[^.;|]{0,14}\\b(?:2500|3500|hd)\\b', E.silverado_cabs, [[2020, 2030, E.gmHD20], [2011, 2019, E.gmHD11], [2001, 2010, E.gmHD01]]],
    ['GMC', 'Canyon', ['canyon'], 'truck', null, ['Extended Cab', 'Crew Cab'], [[2023, 2030, E.gmMid23], [2015, 2022, E.gmMid15], [2004, 2012, E.gmMid04]]],
    ['GMC', 'Yukon', ['yukon', 'yukon xl'], 'suv', null, null, [[2021, 2030, E.gmSUV21], [2015, 2020, E.gmSUV15], [2007, 2014, E.gmSUV07]]],
    ['GMC', 'Acadia', ['acadia'], 'suv', null, null, [[2017, 2030, ['2.0L Turbo', '2.5L I4', '3.6L V6']]]],
    ['GMC', 'Terrain', ['terrain'], 'suv', null, null, [[2010, 2030, E.equinox]]],
    ['Toyota', 'Tacoma', ['tacoma'], 'truck', null, ['Access Cab', 'Double Cab'], [[2024, 2030, E.tacoma24], [2016, 2023, E.tacoma16], [2005, 2015, E.tacoma05]]],
    ['Toyota', 'Tundra', ['tundra'], 'truck', null, ['Regular Cab', 'Double Cab', 'CrewMax'], [[2022, 2030, E.tundra22], [2007, 2021, E.tundra07], [2000, 2006, E.tundra00]]],
    ['Toyota', '4Runner', ['4runner', '4 runner', 'four runner', 'forerunner'], 'suv', '\\b4[\\s-]?runner\\b', null, [[2010, 2030, E.fourrunner], [2003, 2009, E.fourrunner03]]],
    ['Toyota', 'RAV4', ['rav4', 'rav 4', 'rav four'], 'suv', '\\brav[\\s-]?4\\b', null, [[2019, 2030, E.rav4_19], [2013, 2018, E.rav4_13], [2006, 2012, ['2.4L / 2.5L I4', '3.5L V6']]]],
    ['Toyota', 'Highlander', ['highlander'], 'suv', null, null, [[2008, 2030, E.highlander]]],
    ['Toyota', 'Sequoia', ['sequoia'], 'suv', null, null, [[2023, 2030, E.tundra22], [2008, 2022, ['4.6L V8', '5.7L V8']]]],
    ['Toyota', 'Camry', ['camry'], 'car', null, null, [[2018, 2030, E.camry18], [2012, 2017, E.camry12], [2007, 2011, ['2.4L / 2.5L I4', '3.5L V6', 'Hybrid']]]],
    ['Toyota', 'Corolla', ['corolla'], 'car', null, ['Sedan', 'Hatchback'], [[2020, 2030, E.corolla20], [2009, 2019, E.corolla14]]],
    ['Toyota', 'Prius', ['prius'], 'car', null, null, [[2010, 2030, ['1.8L Hybrid', '2.0L Hybrid (23+)']]]],
    ['Toyota', 'Sienna', ['sienna'], 'van', null, null, [[2021, 2030, ['2.5L Hybrid']], [2011, 2020, ['3.5L V6', '2.7L I4 (11-12)']], [1998, 2010, ['3.0L / 3.3L / 3.5L V6']]]],
    ['Toyota', 'FJ Cruiser', ['fj cruiser', 'fj'], 'suv', null, null, [[2007, 2014, ['4.0L V6']]]],
    ['Honda', 'Civic', ['civic'], 'car', null, ['Sedan', 'Coupe', 'Hatchback'], [[2016, 2030, E.civic16], [2012, 2015, E.civic12], [2006, 2011, E.civic06]]],
    ['Honda', 'Accord', ['accord'], 'car', null, ['Sedan', 'Coupe'], [[2018, 2030, E.accord18], [2013, 2017, E.accord13], [2008, 2012, E.accord08]]],
    ['Honda', 'CR-V', ['cr-v', 'crv', 'cr v'], 'suv', '\\bcr[\\s-]?v\\b', null, [[2017, 2030, E.crv17], [2007, 2016, E.crv12]]],
    ['Honda', 'Pilot', ['pilot'], 'suv', null, null, [[2009, 2030, ['3.5L V6']]]],
    ['Honda', 'Ridgeline', ['ridgeline'], 'truck', null, null, [[2006, 2030, ['3.5L V6']]]],
    ['Honda', 'Odyssey', ['odyssey'], 'van', null, null, [[2005, 2030, ['3.5L V6']]]],
    ['Honda', 'HR-V', ['hr-v', 'hrv'], 'suv', '\\bhr[\\s-]?v\\b', null, [[2016, 2030, ['1.8L I4', '2.0L I4 (23+)']]]],
    ['Nissan', 'Frontier', ['frontier'], 'truck', null, ['King Cab', 'Crew Cab'], [[2020, 2030, E.frontier20], [2005, 2019, E.frontier05]]],
    ['Nissan', 'Titan', ['titan', 'titan xd'], 'truck', null, ['King Cab', 'Crew Cab', 'Single Cab'], [[2004, 2030, E.titan]]],
    ['Nissan', 'Altima', ['altima'], 'car', null, null, [[2007, 2030, E.altima]]],
    ['Nissan', 'Sentra', ['sentra'], 'car', null, null, [[2007, 2030, ['1.8L I4', '2.0L I4', '1.6L Turbo (17-19)']]]],
    ['Nissan', 'Maxima', ['maxima'], 'car', null, null, [[2009, 2023, ['3.5L V6']]]],
    ['Nissan', 'Rogue', ['rogue'], 'suv', null, null, [[2008, 2030, E.rogue]]],
    ['Nissan', 'Murano', ['murano'], 'suv', null, null, [[2009, 2030, ['3.5L V6']]]],
    ['Nissan', 'Pathfinder', ['pathfinder'], 'suv', null, null, [[2005, 2030, E.pathfinder]]],
    ['Nissan', 'Armada', ['armada'], 'suv', null, null, [[2005, 2030, ['5.6L V8']]]],
    ['Jeep', 'Wrangler', ['wrangler', 'wrangler unlimited', 'jk', 'jl'], 'suv', null, ['2-door', '4-door (Unlimited)'], [[2018, 2030, E.jl], [2012, 2018, E.jk], [2007, 2011, E.jk07], [1997, 2006, E.tj]]],
    ['Jeep', 'Gladiator', ['gladiator'], 'truck', null, null, [[2020, 2030, E.gladiator]]],
    ['Jeep', 'Grand Cherokee', ['grand cherokee'], 'suv', null, null, [[2022, 2030, E.gc22], [2011, 2021, E.gc11], [2005, 2010, E.gc05]]],
    ['Jeep', 'Cherokee', ['cherokee'], 'suv', null, null, [[2014, 2023, E.cherokee]]],
    ['Jeep', 'Compass', ['compass'], 'suv', null, null, [[2017, 2030, ['2.4L I4', '2.0L Turbo (23+)']]]],
    ['Jeep', 'Renegade', ['renegade'], 'suv', null, null, [[2015, 2023, ['1.3L Turbo', '1.4L Turbo', '2.4L I4']]]],
    ['Jeep', 'Liberty', ['liberty'], 'suv', null, null, [[2002, 2012, ['3.7L V6', '2.8L Diesel (05-06)']]]],
    ['Subaru', 'Outback', ['outback'], 'suv', null, null, [[2010, 2030, E.subaru]]],
    ['Subaru', 'Forester', ['forester'], 'suv', null, null, [[2009, 2030, E.subaru]]],
    ['Subaru', 'Crosstrek', ['crosstrek'], 'suv', null, null, [[2013, 2030, E.subaru]]],
    ['Subaru', 'Impreza', ['impreza'], 'car', null, ['Sedan', 'Hatchback'], [[2008, 2030, E.subaru]]],
    ['Subaru', 'WRX', ['wrx', 'sti'], 'car', null, null, [[2008, 2030, ['2.0L Turbo', '2.4L Turbo', '2.5L Turbo (STI)']]]],
    ['Subaru', 'Ascent', ['ascent'], 'suv', null, null, [[2019, 2030, ['2.4L Turbo']]]],
    ['Hyundai', 'Elantra', ['elantra'], 'car', null, null, [[2007, 2030, E.hyundaiKia]]],
    ['Hyundai', 'Sonata', ['sonata'], 'car', null, null, [[2006, 2030, E.hyundaiKia]]],
    ['Hyundai', 'Tucson', ['tucson'], 'suv', null, null, [[2010, 2030, E.hyundaiKia]]],
    ['Hyundai', 'Santa Fe', ['santa fe', 'santafe'], 'suv', null, null, [[2007, 2030, E.hyundaiKia]]],
    ['Hyundai', 'Palisade', ['palisade'], 'suv', null, null, [[2020, 2030, E.telluride]]],
    ['Kia', 'Sorento', ['sorento'], 'suv', null, null, [[2011, 2030, E.hyundaiKia]]],
    ['Kia', 'Sportage', ['sportage'], 'suv', null, null, [[2011, 2030, E.hyundaiKia]]],
    ['Kia', 'Telluride', ['telluride'], 'suv', null, null, [[2020, 2030, E.telluride]]],
    ['Kia', 'Optima / K5', ['optima', 'k5'], 'car', null, null, [[2011, 2030, E.hyundaiKia]]],
    ['Kia', 'Soul', ['soul'], 'car', null, null, [[2010, 2030, ['1.6L I4', '2.0L I4', '1.6L Turbo']]]],
    ['Kia', 'Forte', ['forte'], 'car', null, null, [[2010, 2030, E.hyundaiKia]]],
    ['Mazda', 'CX-5', ['cx-5', 'cx5', 'cx 5'], 'suv', '\\bcx[\\s-]?5\\b', null, [[2013, 2030, E.mazda]]],
    ['Mazda', 'Mazda3', ['mazda3', 'mazda 3', '3'], 'car', '\\bmazda[\\s-]?3\\b', ['Sedan', 'Hatchback'], [[2010, 2030, E.mazda]]],
    ['Mazda', 'CX-9', ['cx-9', 'cx9'], 'suv', '\\bcx[\\s-]?9\\b', null, [[2016, 2023, ['2.5L Turbo']]]],
    ['Lexus', 'RX', ['rx', 'rx350', 'rx 350'], 'suv', '\\brx[\\s-]?(?:350|450|500|330)?\\b', null, [[2010, 2030, E.lexusRX]]],
    ['Lexus', 'ES', ['es', 'es350', 'es 350'], 'car', '\\bes[\\s-]?(?:350|300|250)\\b', null, [[2013, 2030, ['3.5L V6', '2.5L I4', 'Hybrid']]]],
    ['Lexus', 'GX', ['gx', 'gx460', 'gx 460'], 'suv', '\\bgx[\\s-]?(?:460|470|550)\\b', null, [[2010, 2030, ['4.6L V8', '3.4L Twin-Turbo V6 (24+)']]]],
    ['Acura', 'MDX', ['mdx'], 'suv', null, null, [[2007, 2030, ['3.5L V6', '3.7L V6', '3.0L Turbo (Type S)']]]],
    ['Acura', 'RDX', ['rdx'], 'suv', null, null, [[2013, 2030, ['2.0L Turbo', '3.5L V6 (13-18)']]]],
    ['Acura', 'TLX', ['tlx'], 'car', null, null, [[2015, 2030, ['2.0L Turbo', '2.4L I4', '3.5L V6']]]],
    ['Infiniti', 'Q50', ['q50', 'q 50'], 'car', '\\bq[\\s-]?50\\b', null, [[2014, 2024, ['3.0L Twin-Turbo V6', '3.7L V6', '2.0L Turbo']]]],
    ['Infiniti', 'QX60', ['qx60'], 'suv', null, null, [[2014, 2030, ['3.5L V6', '2.0L Turbo (25+)']]]],
    ['BMW', '3 Series', ['3 series', '328i', '330i', '335i', '340i', 'three series'], 'car', '\\b3[\\s-]?series\\b|\\b3[234]\\d[ie]\\b', ['Sedan', 'Wagon'], [[2012, 2030, E.bmw3]]],
    ['BMW', 'X5', ['x5', 'x 5'], 'suv', '\\bx5\\b', null, [[2014, 2030, ['3.0L Turbo I6', '4.4L Twin-Turbo V8', 'Plug-in Hybrid']]]],
    ['BMW', 'X3', ['x3', 'x 3'], 'suv', '\\bx3\\b', null, [[2011, 2030, ['2.0L Turbo I4', '3.0L Turbo I6']]]],
    ['Mercedes-Benz', 'C-Class', ['c-class', 'c class', 'c300', 'c 300', 'c250'], 'car', '\\bc[\\s-]?(?:class|250|300|350|43|63)\\b', null, [[2015, 2030, E.mbC]]],
    ['Mercedes-Benz', 'E-Class', ['e-class', 'e class', 'e350', 'e 350'], 'car', '\\be[\\s-]?(?:class|350|300|450)\\b', null, [[2010, 2030, ['2.0L Turbo I4', '3.0L V6', '3.5L V6']]]],
    ['Mercedes-Benz', 'GLE / ML', ['gle', 'ml350', 'ml 350', 'ml'], 'suv', '\\b(?:gle|ml)[\\s-]?\\d{0,3}\\b', null, [[2012, 2030, ['3.0L V6', '3.5L V6', '2.0L Turbo']]]],
    ['Mercedes-Benz', 'Sprinter', ['sprinter'], 'van', null, null, [[2007, 2030, ['2.0L / 2.1L Diesel', '3.0L V6 Diesel']]]],
    ['Audi', 'A4', ['a4', 'a 4'], 'car', '\\ba4\\b', null, [[2009, 2030, ['2.0L Turbo']]]],
    ['Audi', 'Q5', ['q5', 'q 5'], 'suv', '\\bq5\\b', null, [[2009, 2030, ['2.0L Turbo', '3.0L V6']]]],
    ['Volkswagen', 'Jetta', ['jetta'], 'car', null, null, [[2011, 2030, E.vw]]],
    ['Volkswagen', 'Golf / GTI', ['golf', 'gti'], 'car', '\\b(?:golf|gti)\\b', null, [[2010, 2030, E.vw]]],
    ['Volkswagen', 'Tiguan', ['tiguan'], 'suv', null, null, [[2009, 2030, ['2.0L Turbo']]]],
    ['Volkswagen', 'Atlas', ['atlas'], 'suv', null, null, [[2018, 2030, ['2.0L Turbo', '3.6L V6']]]],
    ['Tesla', 'Model 3', ['model 3', 'model three'], 'car', '\\bmodel[\\s-]3\\b', null, [[2017, 2030, ['Electric']]]],
    ['Tesla', 'Model Y', ['model y', 'model why'], 'suv', '\\bmodel[\\s-]y\\b', null, [[2020, 2030, ['Electric']]]],
    ['Tesla', 'Model S', ['model s'], 'car', '\\bmodel[\\s-]s\\b', null, [[2012, 2030, ['Electric']]]],
    ['Tesla', 'Model X', ['model x'], 'suv', '\\bmodel[\\s-]x\\b', null, [[2016, 2030, ['Electric']]]],
    ['Tesla', 'Cybertruck', ['cybertruck', 'cyber truck'], 'truck', null, null, [[2024, 2030, ['Electric']]]],
    ['Rivian', 'R1T', ['r1t', 'r1 t'], 'truck', null, null, [[2022, 2030, ['Electric']]]],
    ['Cadillac', 'Escalade', ['escalade'], 'suv', null, null, [[2021, 2030, E.escalade], [2007, 2020, ['6.2L V8']]]],
    ['Lincoln', 'Navigator', ['navigator'], 'suv', null, null, [[2015, 2030, E.navigator], [2007, 2014, ['5.4L V8']]]],
    ['Chrysler', 'Pacifica', ['pacifica'], 'van', null, null, [[2017, 2030, E.pacifica]]],
    ['Chrysler', '300', ['300', 'three hundred'], 'car', '\\bchrysler\\b[^.;|]{0,12}\\b300\\b', null, [[2011, 2023, E.chrysler300], [2005, 2010, ['2.7L V6', '3.5L V6', '5.7L HEMI V8']]]],
    ['Chrysler', 'Town & Country', ['town and country', 'town & country'], 'van', '\\btown\\s*(?:&|and)\\s*country\\b', null, [[2011, 2016, E.caravan11], [2008, 2010, E.caravan08]]],
    ['Buick', 'Enclave', ['enclave'], 'suv', null, null, [[2008, 2030, ['3.6L V6']]]],
    ['Buick', 'Encore', ['encore'], 'suv', null, null, [[2013, 2030, ['1.4L Turbo', '1.2L / 1.3L Turbo (GX)']]]],
    ['Mitsubishi', 'Outlander', ['outlander'], 'suv', null, null, [[2014, 2030, ['2.4L I4', '2.5L I4', '3.0L V6', 'Plug-in Hybrid']]]],
    ['Land Rover', 'Range Rover', ['range rover', 'range rover sport', 'evoque'], 'suv', null, null, [[2013, 2030, ['3.0L Supercharged V6', '5.0L Supercharged V8', '2.0L Turbo', '3.0L Turbo I6']]]],
    ['Land Rover', 'Defender', ['defender'], 'suv', null, null, [[2020, 2030, ['2.0L Turbo', '3.0L Turbo I6', '5.0L V8']]]],
    ['Porsche', 'Cayenne', ['cayenne'], 'suv', null, null, [[2011, 2030, ['3.0L V6', '2.9L / 4.0L Twin-Turbo V8', 'Hybrid']]]],
    ['Porsche', '911', ['911', 'nine eleven'], 'car', '\\b911\\b', ['Coupe', 'Cabriolet'], [[2012, 2030, ['3.0L Twin-Turbo Flat-6', '3.8L / 4.0L Flat-6']]]],
    ['Volvo', 'XC90', ['xc90', 'xc 90'], 'suv', '\\bxc[\\s-]?90\\b', null, [[2016, 2030, ['2.0L Turbo / Supercharged', 'Plug-in Hybrid']]]],
    ['Volvo', 'XC60', ['xc60', 'xc 60'], 'suv', '\\bxc[\\s-]?60\\b', null, [[2010, 2030, ['2.0L Turbo', '3.0L / 3.2L I6 (10-15)']]]],
    ['Hummer', 'H2', ['h2', 'h 2'], 'suv', '\\bh2\\b', null, [[2003, 2009, ['6.0L V8', '6.2L V8']]]],
    ['Hummer', 'H3', ['h3', 'h 3'], 'suv', '\\bh3\\b', null, [[2006, 2010, ['3.5L / 3.7L I5', '5.3L V8']]]],
    ['Isuzu', 'NPR', ['npr'], 'truck', null, null, [[2005, 2030, ['5.2L Diesel', '6.0L V8 gas']]]],
    ['Genesis', 'GV70', ['gv70', 'gv 70'], 'suv', '\\bgv[\\s-]?70\\b', null, [[2022, 2030, ['2.5L Turbo', '3.5L Twin-Turbo V6']]]]
  ];

  // Parts a customer says, mapped to the search we run. Longest phrases are scanned first.
  var PARTS = [
    ['brake pads', 'brake pads', ['brake pad', 'pads', 'front pads', 'rear pads', 'break pads', 'break pad']],
    ['brake rotors', 'brake rotors', ['rotors', 'rotor', 'brake rotor', 'discs', 'brake discs']],
    ['brake calipers', 'brake caliper', ['calipers', 'caliper']],
    ['brake kit', 'brake kit', ['brake kit', 'brakes', 'breaks', 'brake']],
    ['alternator', 'alternator', ['alternator', 'alternators']],
    ['starter', 'starter', ['starter', 'starter motor', 'starters']],
    ['battery', 'battery', ['battery', 'batteries', 'car battery']],
    ['spark plugs', 'spark plugs', ['spark plugs', 'spark plug', 'plugs']],
    ['ignition coil', 'ignition coil', ['ignition coil', 'ignition coils', 'coil pack', 'coil packs']],
    ['water pump', 'water pump', ['water pump']],
    ['radiator', 'radiator', ['radiator', 'radiators']],
    ['thermostat', 'thermostat', ['thermostat']],
    ['fuel pump', 'fuel pump', ['fuel pump', 'fuel pump module']],
    ['fuel filter', 'fuel filter', ['fuel filter']],
    ['oil filter', 'oil filter', ['oil filter', 'oil filters']],
    ['air filter', 'air filter', ['air filter', 'engine air filter', 'air filters', 'intake filter']],
    ['cabin air filter', 'cabin air filter', ['cabin filter', 'cabin air filter']],
    ['cold air intake', 'cold air intake', ['cold air intake', 'air intake', 'intake']],
    ['serpentine belt', 'serpentine belt', ['serpentine belt', 'drive belt', 'belt']],
    ['belt tensioner', 'belt tensioner', ['tensioner', 'belt tensioner']],
    ['timing belt', 'timing belt', ['timing belt']],
    ['timing chain', 'timing chain', ['timing chain']],
    ['oxygen sensor', 'oxygen sensor', ['o2 sensor', 'oxygen sensor', 'o2 sensors', 'oxygen sensors']],
    ['catalytic converter', 'catalytic converter', ['catalytic converter', 'cat converter', 'catalytic']],
    ['muffler', 'muffler', ['muffler', 'mufflers']],
    ['exhaust', 'exhaust', ['exhaust', 'exhaust system', 'cat back', 'cat-back', 'exhaust tip', 'exhaust tips']],
    ['headlights', 'headlight', ['headlights', 'headlight', 'head lights', 'head light', 'headlamps', 'headlamp']],
    ['tail lights', 'tail light', ['tail lights', 'tail light', 'taillights', 'taillight', 'tail lamps', 'tail lamp']],
    ['fog lights', 'fog light', ['fog lights', 'fog light', 'fog lamps']],
    ['light bar', 'light bar', ['light bar', 'led light bar', 'led bar', 'light bars']],
    ['bulbs', 'bulb', ['bulbs', 'bulb', 'headlight bulbs', 'led bulbs', 'light bulbs']],
    ['wiper blades', 'wiper blades', ['wiper blades', 'wipers', 'wiper blade', 'windshield wipers']],
    ['shocks', 'shock absorber', ['shocks', 'shock absorbers', 'shock absorber', 'shock']],
    ['struts', 'strut', ['struts', 'strut', 'strut assembly']],
    ['coil springs', 'coil springs', ['coil springs', 'springs', 'coil spring']],
    ['leaf springs', 'leaf springs', ['leaf springs', 'leaf spring']],
    ['lift kit', 'lift kit', ['lift kit', 'lift', 'suspension lift', 'lift kits']],
    ['leveling kit', 'leveling kit', ['leveling kit', 'level kit', 'levelling kit', 'leveling']],
    ['lowering kit', 'lowering kit', ['lowering kit', 'lowering springs', 'lowering']],
    ['control arms', 'control arm', ['control arms', 'control arm', 'upper control arms', 'lower control arms']],
    ['ball joints', 'ball joint', ['ball joints', 'ball joint']],
    ['tie rods', 'tie rod', ['tie rods', 'tie rod', 'tie rod ends', 'tie rod end']],
    ['sway bar', 'sway bar', ['sway bar', 'sway bar links', 'stabilizer bar', 'sway bar link', 'end links']],
    ['wheel bearings', 'wheel bearing', ['wheel bearings', 'wheel bearing', 'hub assembly', 'hub bearing', 'wheel hub', 'hub']],
    ['cv axle', 'cv axle', ['cv axle', 'cv axles', 'axle', 'axles', 'half shaft', 'drive axle']],
    ['driveshaft', 'driveshaft', ['driveshaft', 'drive shaft', 'u joint', 'u-joint']],
    ['power steering pump', 'power steering pump', ['power steering pump', 'steering pump']],
    ['rack and pinion', 'rack and pinion', ['rack and pinion', 'steering rack']],
    ['ac compressor', 'a/c compressor', ['ac compressor', 'a/c compressor', 'air conditioning compressor', 'compressor']],
    ['condenser', 'condenser', ['condenser', 'ac condenser']],
    ['blower motor', 'blower motor', ['blower motor', 'blower']],
    ['heater core', 'heater core', ['heater core']],
    ['window regulator', 'window regulator', ['window regulator', 'window motor', 'window regulators']],
    ['door handle', 'door handle', ['door handle', 'door handles']],
    ['side mirrors', 'mirror', ['side mirrors', 'side mirror', 'mirrors', 'mirror', 'tow mirrors', 'tow mirror', 'towing mirrors']],
    ['motor mount', 'motor mount', ['motor mount', 'motor mounts', 'engine mount', 'engine mounts']],
    ['transmission mount', 'transmission mount', ['transmission mount']],
    ['valve cover gasket', 'valve cover gasket', ['valve cover gasket', 'valve cover']],
    ['head gasket', 'head gasket', ['head gasket']],
    ['clutch', 'clutch', ['clutch', 'clutch kit']],
    ['flywheel', 'flywheel', ['flywheel']],
    ['floor mats', 'floor mats', ['floor mats', 'floor mat', 'floor liners', 'floor liner', 'mats', 'liners', 'weathertech', 'husky liners', 'all weather mats']],
    ['seat covers', 'seat covers', ['seat covers', 'seat cover']],
    ['tonneau cover', 'tonneau cover', ['tonneau cover', 'tonneau', 'bed cover', 'truck bed cover', 'tunnel cover', 'tono cover', 'tonno cover', 'bed covers']],
    ['bed liner', 'bed liner', ['bed liner', 'bedliner', 'bed mat', 'bed liners']],
    ['running boards', 'running boards', ['running boards', 'running board', 'side steps', 'side step', 'nerf bars', 'nerf bar', 'steps', 'step bars', 'rock sliders']],
    ['bull bar', 'bull bar', ['bull bar', 'grille guard', 'grill guard', 'brush guard', 'push bar']],
    ['bumper', 'bumper', ['bumper', 'bumpers', 'front bumper', 'rear bumper']],
    ['grille', 'grille', ['grille', 'grill', 'front grille', 'front grill']],
    ['hood shield', 'hood shield', ['hood shield', 'bug deflector', 'bug shield', 'hood deflector', 'hood protector', 'bug guard']],
    ['window visors', 'window visor', ['window visors', 'vent visors', 'rain guards', 'wind deflectors', 'window deflectors', 'ventvisor', 'visors']],
    ['mud flaps', 'mud flaps', ['mud flaps', 'mud flap', 'splash guards', 'mudflaps', 'mud guards']],
    ['fender flares', 'fender flares', ['fender flares', 'fender flare', 'flares']],
    ['tow hitch', 'hitch', ['tow hitch', 'trailer hitch', 'hitch', 'receiver hitch', 'hitch receiver']],
    ['trailer wiring', 'trailer wiring', ['trailer wiring', 'trailer harness', 'wiring harness', 'trailer plug']],
    ['roof rack', 'roof rack', ['roof rack', 'roof racks', 'cross bars', 'crossbars', 'rack', 'bed rack', 'ladder rack']],
    ['tool box', 'tool box', ['tool box', 'toolbox', 'truck tool box']],
    ['winch', 'winch', ['winch', 'winches']],
    ['wheels', 'wheels', ['wheels', 'rims', 'wheel', 'rim']],
    ['tires', 'tires', ['tires', 'tire', 'tyres']],
    ['lug nuts', 'lug nuts', ['lug nuts', 'lug nut', 'wheel locks']],
    ['hubcaps', 'hubcap', ['hubcaps', 'hub caps', 'wheel covers']],
    ['dash cover', 'dash cover', ['dash cover', 'dash mat', 'dashboard cover']],
    ['steering wheel cover', 'steering wheel cover', ['steering wheel cover']],
    ['phone mount', 'phone mount', ['phone mount', 'phone holder']],
    ['backup camera', 'backup camera', ['backup camera', 'back up camera', 'rear camera', 'reverse camera']],
    ['dash cam', 'dash cam', ['dash cam', 'dashcam', 'dash camera']],
    ['stereo', 'stereo', ['stereo', 'radio', 'head unit', 'car stereo', 'speakers', 'subwoofer']],
    ['battery charger', 'battery charger', ['battery charger', 'trickle charger', 'jump starter', 'jump pack']],
    ['antenna', 'antenna', ['antenna']],
    ['spare tire carrier', 'spare tire carrier', ['spare tire carrier', 'tire carrier']],
    ['jack', 'jack', ['jack', 'floor jack', 'car jack']],
    ['air suspension', 'air suspension', ['air suspension', 'air bags', 'airbags', 'air springs', 'air lift']],
    ['differential', 'differential', ['differential', 'diff cover', 'differential cover']],
    ['transmission filter', 'transmission filter', ['transmission filter', 'trans filter']],
    ['transmission cooler', 'transmission cooler', ['transmission cooler', 'trans cooler']],
    ['oil pan', 'oil pan', ['oil pan']],
    ['gas cap', 'gas cap', ['gas cap', 'fuel cap']],
    ['fuel injectors', 'fuel injector', ['fuel injectors', 'fuel injector', 'injectors']],
    ['throttle body', 'throttle body', ['throttle body']],
    ['mass air flow sensor', 'mass air flow sensor', ['mass air flow sensor', 'maf sensor', 'maf']],
    ['egr valve', 'egr valve', ['egr valve', 'egr']],
    ['turbo', 'turbocharger', ['turbo', 'turbocharger']],
    ['intercooler', 'intercooler', ['intercooler']],
    ['hood struts', 'lift support', ['hood struts', 'hood lift', 'lift supports', 'hood shocks', 'hatch struts', 'tailgate struts']],
    ['tailgate', 'tailgate', ['tailgate', 'tailgate assist', 'tailgate handle', 'tailgate lock']],
    ['bed extender', 'bed extender', ['bed extender']],
    ['cargo carrier', 'cargo carrier', ['cargo carrier', 'cargo box', 'hitch carrier', 'bike rack', 'cargo basket']],
    ['fifth wheel hitch', 'fifth wheel hitch', ['fifth wheel', '5th wheel', 'gooseneck']],
    ['brake controller', 'brake controller', ['brake controller', 'trailer brake controller']],
    ['tie downs', 'tie down', ['tie downs', 'tie down', 'tie-downs', 'ratchet straps', 'straps']],
    ['horn', 'horn', ['horn', 'air horn']],
    ['wiper motor', 'wiper motor', ['wiper motor']],
    ['door lock actuator', 'door lock actuator', ['door lock actuator', 'lock actuator']],
    ['key fob', 'key fob', ['key fob', 'remote', 'key']],
    ['sensor', 'sensor', ['sensor', 'sensors', 'camshaft sensor', 'crankshaft sensor', 'abs sensor', 'speed sensor', 'tpms', 'tire pressure sensor']],
    ['switch', 'switch', ['switch', 'switches', 'window switch']],
    ['fuse', 'fuse', ['fuse', 'fuses']],
    ['relay', 'relay', ['relay', 'relays']],
    ['wiring', 'wire', ['wiring', 'wire', 'wires', 'harness']],
    ['gasket', 'gasket', ['gasket', 'gaskets', 'seal', 'seals']],
    ['hose', 'hose', ['hose', 'hoses', 'radiator hose', 'coolant hose']],
    ['coolant', 'coolant', ['coolant', 'antifreeze']],
    ['oil', 'motor oil', ['motor oil', 'engine oil', 'oil change']],
    ['cleaner', 'cleaner', ['cleaner', 'wax', 'polish', 'detailing', 'car wash']],
    ['cover', 'car cover', ['car cover', 'truck cover', 'vehicle cover']],
    ['emblem', 'emblem', ['emblem', 'emblems', 'badge', 'badges', 'decal', 'decals']],
    ['hardware', 'hardware', ['hardware', 'bolts', 'clips', 'fasteners']]
  ];

  var FILLER = ['parts for my', 'parts for a', 'parts for the', 'part for my', 'for my', 'for a', 'for the', 'on my', 'i need', 'i need a', 'i need an', 'i need some', 'i want', 'i want a', 'i am looking for', "i'm looking for", 'im looking for', 'looking for', 'find me', 'find', 'show me', 'get me', 'give me', 'search for', 'search', 'do you have', 'do you carry', 'do you sell', 'can you find', 'can i get', 'please', 'hey', 'hi', 'hello', 'ok', 'okay', 'um', 'uh', 'and also', 'also', 'some', 'a set of', 'set of', 'pair of', 'new', 'replacement', 'aftermarket', 'oem', 'a', 'an', 'the', 'my', 'me', 'i', 'to', 'of', 'with', 'it', 'its', "it's", 'need', 'want', 'have', 'got', 'thanks', 'thank you', 'vehicle', 'car', 'truck', 'suv'];

  // ======================================================================= helpers
  function $(s, r) { return (r || document).querySelector(s); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function rx(s, f) { return new RegExp(s, f || 'i'); }
  function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function aliasRe(a) { return '\\b' + a.trim().split(/\s+/).map(escRe).join('[\\s-]*') + '\\b'; }
  function cap(s) { return s.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); }); }
  function plural(s) { return /s$/i.test(s) ? s : (/(?:ss|sh|ch|x)$/i.test(s) ? s + 'es' : s + 's'); }

  var ONES = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 };
  var TENS = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
  function wordsToYears(t) {
    // "twenty fifteen" / "twenty twenty two" / "nineteen ninety nine" / "two thousand fifteen" / "two thousand and eight" / "oh eight"
    t = t.replace(/\b(nineteen|twenty)[\s-]+(ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen)\b/g, function (_, a, b) { return (a === 'nineteen' ? 1900 : 2000) + ONES[b]; });
    t = t.replace(/\b(nineteen|twenty)[\s-]+(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(?:[\s-]+(one|two|three|four|five|six|seven|eight|nine))?\b/g, function (_, a, b, c) { return (a === 'nineteen' ? 1900 : 2000) + TENS[b] + (c ? ONES[c] : 0); });
    t = t.replace(/\btwo[\s-]+thousand(?:[\s-]+and)?(?:[\s-]+(twenty|thirty)(?:[\s-]+(one|two|three|four|five|six|seven|eight|nine))?|[\s-]+(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen))?\b/g, function (_, a, b, c) { return 2000 + (a ? TENS[a] + (b ? ONES[b] : 0) : (c ? ONES[c] : 0)); });
    t = t.replace(/\b(?:oh|o)[\s-]+(one|two|three|four|five|six|seven|eight|nine)\b/g, function (_, a) { return 2000 + ONES[a]; });
    return t;
  }
  function normalize(s) {
    s = ' ' + String(s || '').toLowerCase() + ' ';
    s = s.replace(/(\d),(\d{3})\b/g, '$1$2');                       // 1,500 → 1500
    s = s.replace(/(\d)\.(\d)/g, '$1\u0001$2');                     // keep decimals (5.7) through the punctuation strip
    s = s.replace(/[“”"]/g, '').replace(/[?!.,;:()]+/g, ' ').replace(/\u0001/g, '.').replace(/\s+/g, ' ');
    s = wordsToYears(s);
    s = s.replace(/\bf[\s-]?(150|250|350|450)\b/g, 'f$1').replace(/\b(\d)[\s-]?door\b/g, '$1-door').replace(/\b(two)[\s-]?door\b/g, '2-door').replace(/\b(four)[\s-]?door\b/g, '4-door');
    s = s.replace(/\b(\d)\s*\.\s*(\d)\s*(?:liter|litre|l)\b/g, '$1.$2l').replace(/\b(\d)\s*point\s*(\d)\b/g, '$1.$2');
    return s;
  }

  // ---- vehicle parse
  function findMake(t) {
    var best = null;
    MAKES.forEach(function (m) {
      m[1].forEach(function (a) {
        var r = rx(aliasRe(a)); var x = r.exec(t);
        if (x && (!best || x[0].length > best.len)) best = { make: m[0], len: x[0].length, at: x.index, text: x[0] };
      });
    });
    return best;
  }
  function findModel(t, makeName) {
    var best = null;
    MODELS.forEach(function (md) {
      if (makeName && md[0] !== makeName && !(makeName === 'Dodge' && md[0] === 'Ram')) return;
      md[2].forEach(function (a) {
        if (!makeName && /^\d+$/.test(a)) return;                 // bare numbers only count once we know the make
        if (!makeName && a.length < 3) return;
        var r = rx(aliasRe(a)); var x = r.exec(t);
        if (x && (!best || x[0].length > best.len)) best = { model: md, len: x[0].length, at: x.index, text: x[0] };
      });
    });
    return best;
  }
  function vehicleFrom(text) {
    var t = normalize(text);
    var years = []; t.replace(/\b((?:19[5-9]|20[0-3])\d)\b/g, function (m, y) { years.push(+y); return m; });
    t.replace(/'(\d\d)\b/g, function (m, y) { years.push((+y > 50 ? 1900 : 2000) + +y); return m; });
    var mk = findMake(t);
    var md = mk ? findModel(t, mk.make) : null;
    if (!md) {
      var g = findModel(t, null);
      if (g) { md = g; if (!mk || (mk.make !== g.model[0] && !(mk.make === 'Dodge' && g.model[0] === 'Ram'))) mk = { make: g.model[0], at: g.at, len: 0, text: '' }; }
    }
    var make = mk ? mk.make : (md ? md.model[0] : null);
    if (md && md.model[0] === 'Ram') make = 'Ram';
    var modelName = md ? md.model[1] : null, modelObj = md ? md.model : null;
    if (make && !md) {                                            // unknown model: the word(s) right after the make
      var after = t.slice(mk.at + mk.len).trim().split(' ');
      var got = [];
      for (var i = 0; i < after.length && got.length < 2; i++) {
        var w = after[i];
        if (!w || /^\d{4}$/.test(w) || FILLER.indexOf(w) >= 0 || isPartWord(w) || /^(crew|quad|regular|extended|double|king|mega|access|super|2-door|4-door|unlimited|hemi|diesel|v6|v8|4x4|4wd|2wd|awd|sedan|coupe|hatchback|convertible|wagon|van|truck|car|suv|pickup)$/.test(w)) break;
        got.push(w);
      }
      if (got.length) modelName = cap(got.join(' '));
    }
    var v = null;
    if (make || modelName) {
      v = { year: years.length ? years[years.length - 1] : null, years: years, make: make, model: modelName, obj: modelObj, type: modelObj ? modelObj[3] : null };
      var body = bodyIn(t, v), eng = engineIn(t, v);
      if (body) v.body = body;
      if (eng) v.engine = eng;
    } else if (years.length) {
      v = { year: years[years.length - 1], years: years, make: null, model: null, obj: null, type: null };
    }
    return v;
  }
  function uniqYears(v) { var s = {}; return (v.years || []).filter(function (y) { if (s[y]) return false; s[y] = 1; return true; }); }
  function bodyOptions(v) {
    if (v.obj && v.obj[5]) return v.obj[5].slice();
    if (v.type === 'truck' || (!v.obj && /\b(truck|pickup)\b/.test(normalize(v.model || '')))) return ['Regular Cab', 'Extended Cab', 'Crew Cab'];
    return null;
  }
  function engineOptions(v) {
    if (v.obj && v.obj[6] && v.year) {
      for (var i = 0; i < v.obj[6].length; i++) { var g = v.obj[6][i]; if (v.year >= g[0] && v.year <= g[1]) return g[2].slice(); }
    }
    if (v.obj && v.obj[6] && v.obj[6].length === 1) return v.obj[6][0][2].slice();
    if (v.obj && v.obj[6]) {                                        // known model, year outside our table: union of the nearest generation
      var near = v.obj[6][0]; return near[2].slice();
    }
    if (v.type === 'truck') return ['V6', 'V8', 'Diesel'];
    if (v.type === 'car') return ['4-cylinder', 'V6', 'Hybrid / Electric'];
    if (v.type === 'suv' || v.type === 'van') return ['4-cylinder', 'V6', 'V8', 'Diesel'];
    return ['4-cylinder', 'V6', 'V8', 'Diesel', 'Hybrid / Electric'];
  }
  function bodyIn(t, v) {
    var opts = bodyOptions(v) || [];
    var m = t.match(/\b(regular cab|reg cab|standard cab|single cab|quad cab|crew cab|mega cab|double cab|extended cab|ext cab|king cab|access cab|supercab|super cab|supercrew|super crew|crewmax|crew max|2-door|4-door|unlimited|sedan|coupe|hatchback|hatch|convertible|wagon|cabriolet)\b/);
    if (!m) return null;
    var said = m[1].replace(/\s+/g, ' ');
    var hit = matchOption(said, opts);
    if (hit) return hit;
    return cap(said);
  }
  function engineIn(t, v) {
    var opts = engineOptions(v) || [];
    var m = t.match(/\b(\d\.\dl?)\b|\b(hemi|ecoboost|eco boost|ecodiesel|eco diesel|cummins|duramax|power ?stroke|powerstroke|diesel|hybrid|electric|v6|v8|v10|v-6|v-8|4 cylinder|four cylinder|4-cylinder|4cyl|i4|inline 4|straight 6|inline 6|i6|turbo|supercharged|4xe|392|pentastar|coyote|vortec|boxer)\b/);
    if (!m) return null;
    var said = (m[1] || m[2]).replace('v-', 'v');
    var hit = matchOption(said, opts);
    return hit || cap(said.replace(/l$/, 'L'));
  }
  function matchOption(said, opts) {
    said = said.toLowerCase().replace(/[^a-z0-9. ]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!said || !opts || !opts.length) return null;
    var num = said.match(/\d\.\d/);
    var first = said.split(' ')[0];
    var i, ol;
    for (i = 0; i < opts.length; i++) { if (opts[i].toLowerCase() === said) return opts[i]; }
    if (/not sure|don't know|dont know|no idea|skip|unsure|whatever|any|doesn't matter|doesnt matter/.test(said)) { for (i = 0; i < opts.length; i++) if (/not sure/i.test(opts[i])) return opts[i]; return null; }
    if (num) { for (i = 0; i < opts.length; i++) if (opts[i].toLowerCase().indexOf(num[0]) >= 0) return opts[i]; }
    // first pass: the option literally contains the first word said (double → "Double / Extended Cab", access → "Access Cab")
    if (first.length > 2 && !/^(the|my|its|a)$/.test(first)) { for (i = 0; i < opts.length; i++) { ol = opts[i].toLowerCase(); if (rx('\\b' + escRe(first)).test(ol)) return opts[i]; } }
    // second pass: synonyms
    var SYN = [
      [/^(regular|reg|standard|single|std)\b/, /regular|single|standard/],
      [/^quad\b/, /quad/],
      [/^crewmax\b|^crew max\b/, /crewmax/],
      [/^(crew|super ?crew)\b/, /crew(?!max)/],
      [/^mega\b/, /mega/],
      [/^(double|extended|ext|super ?cab|king|access)\b/, /double|extended|supercab|king|access/],
      [/^(2|two)[\s-]?door\b|^coupe\b/, /2-door|coupe/],
      [/^(4|four)[\s-]?door\b|^unlimited\b|^sedan\b/, /4-door|sedan/],
      [/^(hatch|hatchback)\b/, /hatch/],
      [/^(convertible|cabriolet|cabrio)\b/, /convertible|cabriolet/],
      [/^wagon\b/, /wagon/],
      [/hemi/, /hemi/],
      [/cummins|duramax|power ?stroke|ecodiesel|eco diesel|diesel/, /diesel/],
      [/ecoboost|eco boost/, /ecoboost/],
      [/hybrid|4xe|powerboost|plug.?in/, /hybrid|4xe|plug/],
      [/electric|\bev\b/, /electric/],
      [/^v6\b|six cylinder|6 cylinder|6 cyl/, /v6/],
      [/^v8\b|eight cylinder|8 cylinder|8 cyl/, /v8/],
      [/^v10\b/, /v10/],
      [/4 cylinder|four cylinder|4-cylinder|4cyl|4 cyl|\bi4\b|inline 4/, /i4|4-cyl/],
      [/turbo/, /turbo/],
      [/gas\b|gasoline/, /v6|v8|i4|gas/]
    ];
    for (var k = 0; k < SYN.length; k++) {
      if (!SYN[k][0].test(said)) continue;
      for (i = 0; i < opts.length; i++) { if (SYN[k][1].test(opts[i].toLowerCase())) return opts[i]; }
    }
    if (said.length > 3) { for (i = 0; i < opts.length; i++) if (opts[i].toLowerCase().indexOf(said) >= 0) return opts[i]; }
    return null;
  }

  // ---- parts parse
  var PART_ALIAS = [];
  function partRe(a) {                                              // like aliasRe, but the last word may be plural
    var w = a.trim().split(/\s+/).map(escRe);
    var last = w.pop(); if (!/s$/.test(last)) last += '(?:e?s)?';
    return '\\b' + w.concat([last]).join('[\\s-]*') + '\\b';
  }
  PARTS.forEach(function (p) {
    var seenA = {};
    [p[0], p[1]].concat(p[2]).forEach(function (a) { a = a.toLowerCase(); if (seenA[a]) return; seenA[a] = 1; PART_ALIAS.push({ a: a, re: rx(partRe(a), 'ig'), p: p }); });
  });
  PART_ALIAS.sort(function (a, b) { return b.a.length - a.a.length; });
  var PART_WORDS = {};
  PARTS.forEach(function (p) { p[2].forEach(function (a) { a.split(' ').forEach(function (w) { PART_WORDS[w] = 1; }); }); });
  function isPartWord(w) { return !!PART_WORDS[w]; }
  function partsFrom(text, vehicle) {
    var t = normalize(text);
    if (vehicle) {                                                  // blank out what the vehicle parse consumed
      t = t.replace(/\b(?:19[5-9]|20[0-3])\d\b/g, ' ');
      MAKES.forEach(function (m) { m[1].forEach(function (a) { t = t.replace(rx(aliasRe(a), 'ig'), ' '); }); });
      if (vehicle.obj) vehicle.obj[2].forEach(function (a) { t = t.replace(rx(aliasRe(a), 'ig'), ' '); });
      else if (vehicle.model) t = t.replace(rx(aliasRe(vehicle.model.toLowerCase()), 'ig'), ' ');
    }
    t = t.replace(/\b(regular cab|reg cab|standard cab|single cab|quad cab|crew cab|mega cab|double cab|extended cab|ext cab|king cab|access cab|supercab|super cab|supercrew|super crew|crewmax|crew max|2-door|4-door|unlimited|sedan|coupe|hatchback|convertible|wagon|4x4|4wd|2wd|awd|fwd|rwd|not sure|dont know|don't know)\b/g, ' ');
    t = t.replace(/\b\d\.\dl?\b|\b(hemi|ecoboost|ecodiesel|cummins|duramax|powerstroke|power stroke|diesel|hybrid|electric|v6|v8|v10|4 cylinder|four cylinder|4-cylinder|turbo|pentastar|gas|gasoline)\b/g, ' ');
    var found = [], seen = {};
    PART_ALIAS.forEach(function (pa) {
      pa.re.lastIndex = 0;
      var x;
      while ((x = pa.re.exec(t))) {
        var key = pa.p[0];
        if (!seen[key]) { seen[key] = 1; found.push({ key: key, label: pa.p[0], q: pa.p[1], at: x.index }); }
        t = t.slice(0, x.index) + ' '.repeat(x[0].length) + t.slice(x.index + x[0].length);
        pa.re.lastIndex = 0;
      }
    });
    found.sort(function (a, b) { return a.at - b.at; });
    // whatever is left that isn't filler becomes a free-text part (split on "and" / commas)
    var rest = t;
    FILLER.slice().sort(function (a, b) { return b.length - a.length; }).forEach(function (f) { rest = rest.replace(rx('\\b' + escRe(f) + '\\b', 'g'), ' '); });
    rest.split(/\s+and\s+|\s+plus\s+|\s*,\s*/).forEach(function (chunk) {
      chunk = chunk.replace(/\s+/g, ' ').trim();
      if (!chunk || chunk.length < 3 || chunk.split(' ').length > 4 || /^[\d\s.-]+$/.test(chunk)) return;
      if (/^(cab|door|doors|model|year|make|engine|truck|car|suv|van|vehicle|thing|things|stuff|parts?|one|ones|it|that|this|them|there)$/.test(chunk)) return;
      var key = 'free:' + chunk;
      if (!seen[key]) { seen[key] = 1; found.push({ key: key, label: chunk, q: chunk, at: 9999, free: true }); }
    });
    return found;
  }

  // ---- fitment scoring on product text
  function yearRangesInclude(text, year, anchors) {
    var res = { any: false, fits: false };
    if (!year) return res;
    var near = function (i) { if (!anchors.length) return true; for (var k = 0; k < anchors.length; k++) if (Math.abs(anchors[k] - i) < 70) return true; return false; };
    var four = /\b((?:19|20)\d{2})\b(?:\s*(?:-|–|—|to|thru|through|\/)\s*((?:19|20)\d{2}|\d{2}|up|present|current|newer|later|on)\b)?/g, m;
    while ((m = four.exec(text))) {
      res.any = true;
      var a = +m[1], b = m[2] ? (/^\d{4}$/.test(m[2]) ? +m[2] : /^\d{2}$/.test(m[2]) ? (+m[2] > 50 ? 1900 : 2000) + +m[2] : 2035) : a;
      if (year >= Math.min(a, b) && year <= Math.max(a, b)) res.fits = true;
    }
    var two = /(?:^|[\s(,'])(\d{2})\s*(?:-|–|—|to|thru|through|\/)\s*(\d{2}|up|present|newer)\b/g;
    while ((m = two.exec(text))) {
      if (!near(m.index)) continue;
      var a2 = (+m[1] > 50 ? 1900 : 2000) + +m[1];
      var b2 = /^\d{2}$/.test(m[2]) ? (+m[2] > 50 ? 1900 : 2000) + +m[2] : 2035;
      if (a2 < 1960 || b2 < a2 || b2 - a2 > 40) continue;
      res.any = true;
      if (year >= a2 && year <= b2) res.fits = true;
    }
    return res;
  }
  function makeRe(v) {
    for (var i = 0; i < MAKES.length; i++) if (MAKES[i][0] === v.make) return rx('\\b' + MAKES[i][2] + '\\b', 'ig');
    return v.make ? rx(aliasRe(v.make.toLowerCase()), 'ig') : null;
  }
  function modelRe(v) {
    if (v.obj) return rx(v.obj[4] || v.obj[2].filter(function (a) { return !/^\d+$/.test(a); }).map(aliasRe).join('|'), 'ig');
    if (v.model) return rx(aliasRe(v.model.toLowerCase()), 'ig');
    return null;
  }
  function positions(re, text) { var out = []; if (!re) return out; re.lastIndex = 0; var m; while ((m = re.exec(text)) && out.length < 20) { out.push(m.index); if (!m[0].length) re.lastIndex++; } return out; }
  function normModel(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\b(super duty|hd)\b/g, '').replace(/\s+/g, ' ').trim(); }
  function sameMake(a, b) { a = String(a || '').toLowerCase(); b = String(b || '').toLowerCase(); return a === b || (a === 'ram' && b === 'dodge') || (a === 'dodge' && b === 'ram'); }
  function sameModel(entry, v) {
    if (!entry.model || !v.model) return false;
    var a = normModel(entry.model), b = normModel(v.model);
    if (a === b) return true;
    // "F-250 Super Duty" vs "F-250", "Silverado 2500 / 3500 HD" vs "Silverado 2500", "Sierra 1500" vs "Sierra"
    var ta = a.split(' '), tb = b.split(' ');
    return ta[0] === tb[0] && (ta.length === 1 || tb.length === 1 || ta[1] === tb[1] || a.indexOf(b) >= 0 || b.indexOf(a) >= 0);
  }
  // structured fitment (the dna.fitment metafield written by fitment_extract.py) → same result shape as fitment()
  function fitStructured(fit, v) {
    if (!fit || !v || !v.make || fit.vehicle === false) return null;   // vehicle === false: not a vehicle part (RV, marine, chemicals…)
    var fits = fit.fits || [];
    if (!fits.length) return fit.universal ? { score: 1, label: 'Universal fit', tone: 'maybe' } : null;
    var short = badgeName(v), best = null;
    fits.forEach(function (e) {
      var r;
      if (!sameMake(e.make, v.make)) return;
      var yrs = e.years || [], inYear = v.year && yrs.some(function (y) { return v.year >= y[0] && v.year <= y[1]; });
      if (sameModel(e, v)) {
        if (!yrs.length) r = { score: 3.5, label: 'Lists ' + short + ' · check year', tone: 'maybe' };
        else if (inYear) r = { score: 5, label: 'Fits your ' + short, tone: 'fit' };
        else r = { score: 1, label: short + ' · other years (' + yrs.map(function (y) { return y[0] === y[1] ? y[0] : y[0] + '–' + (y[1] >= 2030 ? '' : y[1]); }).join(', ') + ')', tone: 'no' };
      } else if (!e.model) {
        r = inYear ? { score: 2.5, label: v.make + ' · check model', tone: 'maybe' } : yrs.length ? { score: 0.8, label: 'Check fitment', tone: 'no' } : { score: 1.5, label: v.make + ' · check fitment', tone: 'maybe' };
      } else {
        r = { score: 0.5, label: v.make + ' · other models', tone: 'no' };
      }
      if (!best || r.score > best.score) best = r;
    });
    return best || { score: 0, label: 'Listed for other vehicles', tone: 'no' };
  }
  function fitment(item, v) {
    if (item.fit && item.fit.vehicle === false) return { score: 0, label: '', tone: 'none' };
    var structured = fitStructured(item.fit, v);
    if (structured) {
      if (v.body && structured.score >= 3.5) {
        var txt = (item.t + ' ' + (item.d || '')).toLowerCase();
        if (matchOptionIn(txt, v.body)) { structured.score += 0.5; structured.label += ' · ' + v.body; }
      }
      if (v.engine && structured.score >= 3.5) {
        var num2 = v.engine.match(/\d\.\d/), kw2 = v.engine.toLowerCase().match(/hemi|ecoboost|ecodiesel|cummins|duramax|power stroke|diesel|hybrid|v6|v8|turbo/);
        var txt2 = (item.t + ' ' + (item.d || '')).toLowerCase();
        if ((num2 && rx('\\b' + escRe(num2[0]) + '\\s*-?(?:l\\b|liter|litre)').test(txt2)) || (kw2 && rx('\\b' + kw2[0] + '\\b').test(txt2))) { structured.score += 0.5; structured.label += ' · ' + v.engine; }
      }
      return structured;
    }
    var text = (item.t + ' | ' + (item.ty || '') + ' | ' + (item.tags || '') + ' | ' + (item.d || '')).toLowerCase();
    var mkPos = positions(makeRe(v), text), mdPos = positions(modelRe(v), text);
    var yr = yearRangesInclude(text, v.year, mdPos.length ? mdPos : mkPos);
    var universal = /\buniversal\b|\bfits most\b|\ball vehicles\b/.test(text);
    var score, label, tone;
    var short = badgeName(v);
    if (mdPos.length) {
      if (yr.fits) { score = 5; label = 'Fits your ' + short; tone = 'fit'; }
      else if (!yr.any) { score = 3.5; label = 'Lists ' + short + ' · check year'; tone = 'maybe'; }
      else { score = 1; label = short + ' · other years'; tone = 'no'; }
    } else if (mkPos.length) {
      if (yr.fits) { score = 2.5; label = v.make + ' · check model'; tone = 'maybe'; }
      else if (!yr.any) { score = 1.5; label = v.make + ' · check fitment'; tone = 'maybe'; }
      else { score = 0.8; label = 'Check fitment'; tone = 'no'; }
    } else if (universal) { score = 1; label = 'Universal fit'; tone = 'maybe'; }
    else if (ANY_MAKE.test(text) || yr.any) { score = 0; label = 'Listed for other vehicles'; tone = 'no'; }
    else { score = 0; label = 'Fitment not listed'; tone = 'none'; }
    // cab / engine refinements
    var notes = [];
    if (v.body && score >= 1) {
      var bl = v.body.toLowerCase().replace(/\s*\(.*\)/, '');
      var cabWords = text.match(/\b(regular cab|quad cab|crew cab|mega cab|double cab|extended cab|king cab|access cab|supercab|supercrew|crewmax|2-door|2 door|4-door|4 door|unlimited|sedan|coupe|hatchback|convertible)\b/g);
      if (cabWords) {
        var hit = cabWords.some(function (w) { return matchOption(w, [v.body]); });
        if (hit) { score += 0.5; notes.push(v.body); } else if (score >= 3.5) { score -= 1; notes.push('listed for other cabs'); }
      }
    }
    if (v.engine && score >= 1) {
      var num = v.engine.match(/\d\.\d/), kw = v.engine.toLowerCase().match(/hemi|ecoboost|ecodiesel|cummins|duramax|power stroke|diesel|hybrid|v6|v8|turbo/);
      if ((num && rx('\\b' + escRe(num[0]) + '\\s*-?(?:l\\b|liter|litre)').test(text)) || (kw && rx('\\b' + kw[0] + '\\b').test(text))) { score += 0.5; notes.push(v.engine); }
    }
    return { score: score, label: label + (notes.length ? ' · ' + notes.join(' · ') : ''), tone: tone };
  }
  var ANY_MAKE = rx('\\b(?:' + MAKES.map(function (m) { return m[2]; }).join('|') + ')\\b');
  function matchOptionIn(text, body) {
    var words = text.match(/\b(regular cab|quad cab|crew cab|mega cab|double cab|extended cab|king cab|access cab|supercab|supercrew|crewmax|2-door|2 door|4-door|4 door|unlimited|sedan|coupe|hatchback|convertible)\b/g);
    return !!(words && words.some(function (w) { return matchOption(w, [body]); }));
  }
  function shortName(v) { return [v.year, v.make === 'Ram' && !v.model ? 'Ram' : v.make, v.model].filter(Boolean).join(' '); }
  function badgeName(v) { return [v.year, v.model ? (v.make === 'Ram' ? 'Ram ' + v.model : v.model) : v.make].filter(Boolean).join(' '); }
  function fullName(v) { return [shortName(v), v.body, v.engine].filter(Boolean).join(' · '); }

  // ======================================================================= search
  var cache = {};
  function searchPart(q) {
    if (cache[q]) return cache[q];
    var url = function (p) { return '/search?view=dna-json&type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q) + '&page=' + p; };
    var get = function (p) { return fetch(url(p), { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('search ' + r.status); return r.json(); }); };
    cache[q] = get(1).then(function (first) {
      var pages = Math.min(first.pages || 1, 6);
      var rest = []; for (var p = 2; p <= pages; p++) rest.push(get(p).catch(function () { return { items: [] }; }));
      return Promise.all(rest).then(function (more) {
        var items = first.items.slice(); more.forEach(function (j) { items = items.concat(j.items || []); });
        var seen = {}; items = items.filter(function (it) { if (seen[it.h]) return false; seen[it.h] = 1; return true; });
        return { count: items.length, items: items, truncated: (first.pages || 1) > 6 };
      });
    }).catch(function (e) { delete cache[q]; throw e; });
    return cache[q];
  }
  function rank(items, part, v) {
    var words = part.q.toLowerCase().split(/\s+/).map(function (w) { return w.replace(/s$/, ''); }).filter(Boolean);
    return items.map(function (it, i) {
      var head = (it.t + ' ' + (it.ty || '')).toLowerCase();
      var titleHit = words.every(function (w) { return head.indexOf(w) >= 0; }) ? 1 : 0;
      var f = fitment(it, v);
      return { it: it, i: i, titleHit: titleHit, fit: f };
    }).sort(function (a, b) { return (b.titleHit - a.titleHit) || (b.fit.score - a.fit.score) || (a.i - b.i); });
  }

  // ======================================================================= UI
  var root, log, input, micBtn, sendBtn, statusEl, sheetOpen = false, lastInputWasVoice = false, speakBack = true;
  var state = null;
  var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  var supported = !!SR;
  var POPULAR = ['Brake pads', 'Rotors', 'Floor mats', 'Tonneau cover', 'Running boards', 'Headlights', 'Shocks', 'Air filter', 'Alternator', 'Battery', 'Lift kit', 'Mud flaps'];

  function loadVehicle() { try { var v = JSON.parse(localStorage.getItem('dna_vehicle') || 'null'); if (v && v.make) { v.obj = null; MODELS.forEach(function (m) { if (m[0] === v.make && m[1] === v.model) v.obj = m; }); if (v.obj) v.type = v.obj[3]; return v; } } catch (e) { /* ignore */ } return null; }
  function saveVehicle(v) { if (v && v.make) garageAdd({ year: v.year, make: v.make, model: v.model, body: v.body, engine: v.engine, type: v.type }); }
  function clearVehicle() { try { localStorage.removeItem('dna_vehicle'); } catch (e) { /* ignore */ } }

  function ensureSheet() {
    if (root) return;
    root = $('#dna-voice');
    if (!root) return;
    log = $('.dna-va__log', root); input = $('.dna-va__input', root); micBtn = $('.dna-va__mic', root); sendBtn = $('.dna-va__send', root); statusEl = $('.dna-va__status', root);
    $('.dna-va__close', root).addEventListener('click', function () { close(true); });
    $('.dna-va__speak', root).addEventListener('click', function (ev) { speakBack = !speakBack; ev.currentTarget.classList.toggle('is-off', !speakBack); ev.currentTarget.setAttribute('aria-pressed', speakBack ? 'true' : 'false'); if (!speakBack) stopSpeaking(); });
    $('.dna-va__reset', root).addEventListener('click', function () { clearVehicle(); start({ fresh: true }); });
    $('form', root).addEventListener('submit', function (ev) { ev.preventDefault(); var t = input.value.trim(); if (!t) return; input.value = ''; lastInputWasVoice = false; handleUtterance(t); });
    micBtn.addEventListener('click', function () { if (listening) stopListening(); else listen(); });
    if (!supported) { micBtn.hidden = true; }
    log.addEventListener('click', function (ev) {
      var b = ev.target.closest('[data-answer]');
      if (b) { ev.preventDefault(); var row = b.closest('.dna-va__opts'); if (row) row.querySelectorAll('button').forEach(function (x) { x.disabled = true; }); b.classList.add('is-picked'); answer(b.getAttribute('data-answer'), b.getAttribute('data-q')); return; }
      var add = ev.target.closest('[data-add]');
      if (add) { ev.preventDefault(); quickAdd(add); return; }
      var more = ev.target.closest('[data-more]');
      if (more) { ev.preventDefault(); var sec = more.closest('.dna-va__results'); sec.classList.add('is-all'); more.remove(); return; }
    });
    window.addEventListener('popstate', function () { if (sheetOpen) close(false); });
  }
  function open(opts) {
    ensureSheet(); if (!root) return;
    opts = opts || {};
    if (!sheetOpen) {
      sheetOpen = true; root.hidden = false; document.body.classList.add('dna-va-open');
      try { history.pushState({ dnaVoice: 1 }, ''); } catch (e) { /* ignore */ }
    }
    if (!state || opts.fresh) start({ fresh: !!opts.fresh, quiet: !!opts.text || !!opts.listen });
    if (opts.text) { lastInputWasVoice = false; handleUtterance(opts.text); }
    else if (opts.listen) listen();
    else focusInput();
  }
  function close(pop) {
    if (!sheetOpen) return;
    sheetOpen = false; root.hidden = true; document.body.classList.remove('dna-va-open');
    stopListening(); stopSpeaking();
    if (pop) { try { if (history.state && history.state.dnaVoice) history.back(); } catch (e) { /* ignore */ } }
  }
  function focusInput() { try { if (!/iP(hone|ad)/.test(navigator.userAgent)) input.focus(); } catch (e) { /* ignore */ } }

  function start(o) {
    o = o || {};
    state = { vehicle: o.fresh ? null : loadVehicle(), parts: [], asked: {}, pending: null, savedUsed: false };
    log.innerHTML = '';
    if (state.vehicle) state.savedUsed = true;
    if (!o.quiet) {
      if (state.vehicle) say('Hi! Still shopping for your <b>' + esc(shortName(state.vehicle)) + '</b>? Tell me the parts you need — or say a different vehicle.', ['Different vehicle']);
      else say('Tell me your vehicle and the parts you need, in one go. Like: <i>"parts for my 2015 Dodge Ram 1500 — find me an alternator and brake pads."</i>', null, true);
    }
  }

  function say(html, opts, muted) {
    var el = document.createElement('div'); el.className = 'dna-va__msg dna-va__msg--bot';
    el.innerHTML = '<div class="dna-va__bubble">' + html + '</div>';
    if (opts && opts.length) el.appendChild(optRow(opts, state.pending ? state.pending.q : 'misc'));
    log.appendChild(el); scrollLog();
    if (!muted && lastInputWasVoice) speak(el.querySelector('.dna-va__bubble').textContent);
    return el;
  }
  function said(text) {
    var el = document.createElement('div'); el.className = 'dna-va__msg dna-va__msg--me';
    el.innerHTML = '<div class="dna-va__bubble">' + esc(text) + '</div>';
    log.appendChild(el); scrollLog(); return el;
  }
  function optRow(opts, q) {
    var row = document.createElement('div'); row.className = 'dna-va__opts';
    row.innerHTML = opts.map(function (o) { return '<button type="button" data-answer="' + esc(o) + '" data-q="' + esc(q) + '">' + esc(o) + '</button>'; }).join('');
    return row;
  }
  function scrollLog() { requestAnimationFrame(function () { log.scrollTop = log.scrollHeight; }); }
  function setStatus(t) { statusEl.textContent = t || ''; }

  // ---- speech in
  var rec = null, listening = false, finalText = '', interimEl = null, listenTimer = 0;
  function listen() {
    if (!supported || listening) return;
    stopSpeaking();
    try {
      rec = new SR(); rec.lang = 'en-US'; rec.interimResults = true; rec.maxAlternatives = 1; rec.continuous = false;
    } catch (e) { setStatus('Voice search is not available here — type instead.'); return; }
    finalText = ''; listening = true; micBtn.classList.add('is-listening'); root.classList.add('is-listening'); setStatus('Listening… say your vehicle and the parts you need');
    interimEl = said('…'); interimEl.classList.add('is-interim');
    rec.onresult = function (ev) {
      var interim = '', fin = '';
      for (var i = ev.resultIndex; i < ev.results.length; i++) { var r = ev.results[i]; if (r.isFinal) fin += r[0].transcript; else interim += r[0].transcript; }
      if (fin) finalText += fin;
      var shown = (finalText + ' ' + interim).trim();
      if (shown) interimEl.querySelector('.dna-va__bubble').textContent = shown;
    };
    rec.onerror = function (ev) {
      var code = ev && ev.error;
      if (code === 'not-allowed' || code === 'service-not-allowed') setStatus('Microphone blocked — allow it in your browser settings, or type below.');
      else if (code === 'no-speech') setStatus("Didn't catch that — tap the mic and try again, or type below.");
      else if (code === 'network') setStatus('Voice needs a connection — try again or type below.');
      else if (code !== 'aborted') setStatus('Voice hiccup (' + code + ') — try again or type below.');
    };
    rec.onend = function () { finishListening(); };
    try { rec.start(); } catch (e) { listening = false; micBtn.classList.remove('is-listening'); root.classList.remove('is-listening'); setStatus('Could not start the microphone — type below.'); if (interimEl) interimEl.remove(); interimEl = null; }
    clearTimeout(listenTimer); listenTimer = setTimeout(function () { try { rec && rec.stop(); } catch (e) { /* ignore */ } }, 12000);
  }
  function stopListening() { clearTimeout(listenTimer); if (rec) { try { rec.stop(); } catch (e) { /* ignore */ } } }
  function finishListening() {
    clearTimeout(listenTimer);
    listening = false; micBtn.classList.remove('is-listening'); root.classList.remove('is-listening');
    var text = (finalText || (interimEl && interimEl.querySelector('.dna-va__bubble').textContent !== '…' ? interimEl.querySelector('.dna-va__bubble').textContent : '')).trim();
    if (interimEl) { interimEl.remove(); interimEl = null; }
    rec = null;
    if (!text) { if (!statusEl.textContent) setStatus("Didn't catch that — tap the mic and try again, or type below."); return; }
    setStatus(''); lastInputWasVoice = true;
    handleUtterance(text);
  }
  // ---- speech out
  function speak(text) {
    if (!speakBack || !('speechSynthesis' in window) || !text) return;
    try { window.speechSynthesis.cancel(); var u = new SpeechSynthesisUtterance(text.replace(/\s+/g, ' ').trim().slice(0, 220)); u.lang = 'en-US'; u.rate = 1.03; window.speechSynthesis.speak(u); } catch (e) { /* ignore */ }
  }
  function stopSpeaking() { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* ignore */ } }

  // ---- conversation
  function handleUtterance(text) {
    said(text);
    var t = normalize(text);
    if (/^\s*(different vehicle|change vehicle|new vehicle|another vehicle|not that one)\s*$/.test(t)) { state.vehicle = null; state.savedUsed = false; state.asked = {}; clearVehicle(); next(); return; }
    if (/^\s*(start over|reset|clear)\s*$/.test(t)) { clearVehicle(); start({ fresh: true }); return; }
    // an answer to the pending question?
    if (state.pending) {
      var opts = state.pending.opts.concat(['Not sure']);
      var hit = matchOption(t, opts);
      var v0 = vehicleFrom(text), p0 = partsFrom(text, v0 && v0.make ? v0 : state.vehicle);
      if (hit && !(v0 && v0.make) && !p0.length) { answer(hit, state.pending.q); return; }
    }
    var v = vehicleFrom(text);
    if (v && v.make) {
      if (uniqYears(v).length > 1 && !v.model) { /* fine */ }
      if (uniqYears(v).length > 1) { state.pending = { q: 'year', opts: uniqYears(v).map(String) }; state.vehicle = v; say('One vehicle at a time — which year is it?', uniqYears(v).map(String)); return; }
      var firstMk = findMake(t);
      var second = firstMk ? findMake(t.replace(rx(aliasRe(firstMk.text), 'i'), ' ')) : null;
      if (second && second.make !== v.make && !(v.make === 'Ram' && second.make === 'Dodge') && !(v.make === 'Dodge' && second.make === 'Ram') && findModel(t, second.make)) { say('One vehicle at a time — which one are we shopping for?', [cap((v.year ? v.year + ' ' : '') + v.make + ' ' + (v.model || '')).trim(), second.make]); state.pending = { q: 'which', opts: [] }; state.vehicle = v; return; }
      var keep = state.vehicle;
      if (keep && keep.make === v.make && (keep.model === v.model || !v.model)) { v.model = v.model || keep.model; v.obj = v.obj || keep.obj; v.type = v.type || keep.type; v.year = v.year || keep.year; v.body = v.body || keep.body; v.engine = v.engine || keep.engine; }
      state.vehicle = v; state.savedUsed = false; state.asked = {};
    } else if (v && v.year && state.vehicle && !state.vehicle.year) {
      state.vehicle.year = v.year;
    } else if (v && v.year && !state.vehicle) {
      state.vehicle = v;
    }
    if (state.vehicle) {
      var b = bodyIn(t, state.vehicle); if (b) { state.vehicle.body = b; state.asked.body = true; }
      var e = engineIn(t, state.vehicle); if (e) { state.vehicle.engine = e; state.asked.engine = true; }
    }
    var parts = partsFrom(text, v && v.make ? v : (state.vehicle && !state.pending ? state.vehicle : null));
    if (parts.length) {
      parts.forEach(function (p) { if (!state.parts.some(function (x) { return x.key === p.key; })) state.parts.push(p); });
    }
    if (!v && !parts.length && state.pending && state.pending.q === 'model') { state.vehicle.model = cap(t.trim()); state.vehicle.obj = null; }
    if (!v && !parts.length && !state.pending) { say("I didn't catch a vehicle or a part in that. Try something like <i>\"2018 F-150 tonneau cover\"</i>.", null); return; }
    state.pending = null;
    next();
  }
  function answer(val, q) {
    said(val);
    var v = state.vehicle;
    if (q === 'year') { var y = parseInt(val, 10); if (y) v.year = y; }
    else if (q === 'model') { if (val === 'Something else') { state.pending = { q: 'model', opts: [] }; say('Type or say the model.', null); focusInput(); return; } v.model = val; v.obj = null; MODELS.forEach(function (m) { if (m[0] === v.make && m[1] === val) v.obj = m; }); if (v.obj) v.type = v.obj[3]; }
    else if (q === 'body') { state.asked.body = true; if (val !== 'Not sure') v.body = val; }
    else if (q === 'engine') { state.asked.engine = true; if (val !== 'Not sure') v.engine = val; }
    else if (q === 'parts') { var p = partsFrom(val, null); (p.length ? p : [{ key: 'free:' + val, label: val, q: val, free: true }]).forEach(function (x) { if (!state.parts.some(function (k) { return k.key === x.key; })) state.parts.push(x); }); }
    else if (q === 'which') { var vv = vehicleFrom(val); if (vv && vv.make) { vv.year = vv.year || (v && v.year); state.vehicle = vv; } }
    else if (q === 'misc' || q === 'vehicle') {
      if (val === 'Different vehicle') { state.vehicle = null; state.savedUsed = false; state.asked = {}; clearVehicle(); }
      else if (/^no vehicle/i.test(val)) { window.location.href = '/search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(state.parts.map(function (p) { return p.q; }).join(' ')); return; }
    }
    state.pending = null;
    next();
  }
  function next() {
    var v = state.vehicle;
    lastStepVoice = lastInputWasVoice;
    if (!v || !v.make) {
      state.pending = { q: 'vehicle', opts: [] };
      say(state.parts.length ? 'Which vehicle is it for? Say the year, make and model.' : 'Which vehicle is it for? Say the year, make and model — and the parts you need.', state.parts.length ? ['No vehicle — just search'] : null);
      return;
    }
    if (!v.model) {
      var models = MODELS.filter(function (m) { return m[0] === v.make || (v.make === 'Dodge' && m[0] === 'Ram'); }).map(function (m) { return m[1]; }).slice(0, 10);
      if (models.length) { state.pending = { q: 'model', opts: models }; say('Which ' + esc(v.make) + ' is it?', models.concat(['Something else'])); return; }
      state.pending = { q: 'model', opts: [] }; say('Which ' + esc(v.make) + ' model is it?', null); return;
    }
    if (!v.year) {
      var ys = []; for (var y = new Date().getFullYear() + 1; y >= 1985; y--) ys.push(String(y));
      state.pending = { q: 'year', opts: ys };
      say('What year is your ' + esc(v.make + ' ' + v.model) + '?', ys); return;
    }
    if (!state.parts.length) {
      state.pending = { q: 'parts', opts: POPULAR };
      say('Got it — <b>' + esc(shortName(v)) + '</b>. What parts do you need for it?', POPULAR); return;
    }
    var bo = bodyOptions(v);
    if (bo && !v.body && !state.asked.body) {
      state.pending = { q: 'body', opts: bo };
      var isCab = /cab/i.test(bo[0]);
      say((isCab ? 'Which cab is your ' : 'Which body style is your ') + esc(shortName(v)) + '? ' + (isCab ? '' : ''), bo.concat(['Not sure'])); return;
    }
    var eo = engineOptions(v);
    if (eo && !v.engine && !state.asked.engine) {
      state.pending = { q: 'engine', opts: eo };
      say('And which engine?', eo.concat(['Not sure'])); return;
    }
    saveVehicle(v);
    showResults();
  }
  var lastStepVoice = false;

  // ---- results
  function showResults() {
    var v = state.vehicle;
    var head = say('Here\'s what we carry for your <b>' + esc(fullName(v)) + '</b>:', null, true);
    if (lastInputWasVoice) speak('Here is what we carry for your ' + shortName(v) + '.');
    if (state.parts.length > 1) {
      var jump = document.createElement('div'); jump.className = 'dna-va__jump';
      jump.innerHTML = state.parts.map(function (p, i) { return '<a href="#dna-va-part-' + i + '">' + esc(cap(p.label)) + '</a>'; }).join('');
      head.appendChild(jump);
      jump.addEventListener('click', function (ev) { var a = ev.target.closest('a'); if (!a) return; ev.preventDefault(); var t = $(a.getAttribute('href')); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
    }
    state.parts.forEach(function (p, i) {
      var sec = document.createElement('section'); sec.className = 'dna-va__results'; sec.id = 'dna-va-part-' + i;
      sec.innerHTML = '<h3>' + esc(cap(plural(p.label))) + ' <span class="dna-va__loading">Searching…</span></h3>';
      log.appendChild(sec); scrollLog();
      searchPart(p.q).then(function (res) { renderPart(sec, p, res); }).catch(function () {
        sec.innerHTML = '<h3>' + esc(cap(plural(p.label))) + '</h3><p class="dna-va__note">Search hiccup — <a href="/search?q=' + encodeURIComponent(p.q) + '&type=product">open the search page</a>.</p>';
      });
    });
    var tail = document.createElement('div'); tail.className = 'dna-va__msg dna-va__msg--bot';
    tail.innerHTML = '<div class="dna-va__bubble">Need something else for it? Just say the part.</div>';
    log.appendChild(tail);
    state.pending = { q: 'parts', opts: POPULAR }; state.parts = [];
    state.done = true;
  }
  function money(cents) { return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function priceLine(it) {
    var off = it.cp && it.cp > it.p ? Math.round((1 - it.p / it.cp) * 100) : 0;
    var pro = window.DNA_PRO && window.DNA_PRO.customer, mine = pro && it.pp ? it.pp[String(pro.tier)] : null;
    if (typeof mine === 'number' && mine * 100 < it.p) return '<div class="dna-va__price dna-va__price--pro">' + money(Math.round(mine * 100)) + ' <s>' + money(it.p) + '</s></div>';
    return '<div class="dna-va__price">' + money(it.p) + (off ? ' <s>' + money(it.cp) + '</s>' : '') + '</div>';
  }
  function renderPart(sec, p, res) {
    var v = state.vehicle;
    var ranked = rank(res.items, p, v);
    var fits = ranked.filter(function (r) { return r.fit.tone === 'fit'; }).length;
    var maybe = ranked.filter(function (r) { return r.fit.tone === 'maybe'; }).length;
    var label = cap(plural(p.label));
    var contact = '/pages/contact?topic=source&part=' + encodeURIComponent(p.label) + '&vehicle=' + encodeURIComponent(fullName(v));
    if (!ranked.length) {
      sec.innerHTML = '<h3>' + esc(label) + '</h3><p class="dna-va__note">We don\'t list ' + esc(p.label) + ' for a ' + esc(shortName(v)) + ' yet — our parts team can usually source it in a day or two.</p>' +
        '<div class="dna-va__opts"><a class="dna-va__ask" href="' + contact + '">Ask us to find it</a></div>';
      return;
    }
    var sub = fits ? fits + ' list' + (fits === 1 ? 's' : '') + ' your ' + esc(shortName(v)) + (ranked.length - fits ? ' · ' + (ranked.length - fits) + ' more to check' : '')
                   : 'None list your ' + esc(shortName(v)) + ' outright' + (maybe ? ' — ' + maybe + ' may fit, check the listing' : '');
    var html = '<h3>' + esc(label) + '</h3><p class="dna-va__sub">' + sub + '</p><div class="dna-va__grid">';
    ranked.forEach(function (r, i) {
      var it = r.it;
      var off = it.cp && it.cp > it.p ? Math.round((1 - it.p / it.cp) * 100) : 0;
      html += '<div class="dna-va__card' + (i >= 8 ? ' dna-va__card--more' : '') + '">' +
        '<div class="dna-va__tile"><a class="dna-va__media" href="/products/' + esc(it.h) + '">' + (it.img ? '<img src="' + esc(it.img) + '" alt="" loading="lazy">' : '') + (off ? '<span class="dna-va__off">' + off + '% off</span>' : '') + '</a>' +
        (it.av && it.vid ? '<button type="button" class="dna-va__add" data-add="' + esc(it.vid) + '" data-multi="' + (it.nv > 1 ? 1 : 0) + '" data-handle="' + esc(it.h) + '" aria-label="Add to cart">+</button>' : '') + '</div>' +
        priceLine(it) +
        '<a class="dna-va__title" href="/products/' + esc(it.h) + '">' + esc(it.t) + '</a>' +
        (r.fit.label ? '<div class="dna-va__fit dna-va__fit--' + r.fit.tone + '">' + esc(r.fit.label) + '</div>' : '') +
        (it.av ? '' : '<div class="dna-va__soldout">Sold out</div>') +
        '</div>';
    });
    html += '</div>';
    if (ranked.length > 8) html += '<button type="button" class="dna-va__more" data-more>Show all ' + ranked.length + '</button>';
    html += '<div class="dna-va__links"><a href="/search?q=' + encodeURIComponent(p.q) + '&type=product">Open in search</a>' + (fits ? '' : '<a href="' + contact + '">Ask us to find the right one</a>') + '</div>';
    sec.innerHTML = html;
  }
  function quickAdd(btn) {
    if (btn.getAttribute('data-multi') === '1') { window.location.href = '/products/' + btn.getAttribute('data-handle'); return; }
    btn.disabled = true; btn.textContent = '…';
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: +btn.getAttribute('data-add'), quantity: 1 }] }) })
      .then(function (r) { if (!r.ok) throw new Error('add'); return fetch('/cart.js', { credentials: 'same-origin' }); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        btn.textContent = '✓'; btn.classList.add('is-added'); setTimeout(function () { btn.disabled = false; btn.textContent = '+'; btn.classList.remove('is-added'); }, 1600);
        var badge = $('.dna-mnav__badge'); if (badge) { badge.textContent = cart.item_count; badge.hidden = !cart.item_count; }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        var cd = $('cart-drawer'); if (cd && typeof cd.renderContents !== 'function') { /* leave the drawer alone */ }
      })
      .catch(function () { btn.disabled = false; btn.textContent = '+'; setStatus('Could not add that one — open the product page.'); });
  }


  // ======================================================================= the garage: saved vehicles + "Shop for my vehicle"
  // Vehicles live on the phone (localStorage dna_garage = { vehicles: [...], active: key }); dna_vehicle mirrors the active one
  // for everything else (badges, fit box, assistant). When the store's app proxy is on (section setting), a signed-in
  // customer's garage is also kept on the server (/apps/garage) so it follows them to other devices.
  var GARAGE_KEY = 'dna_garage';
  function vehicleKey(v) { return [v.year || '', v.make || '', v.model || '', v.body || '', v.engine || ''].join('|').toLowerCase(); }
  function stripVehicle(v) { return { year: v.year || null, make: v.make, model: v.model || null, body: v.body || null, engine: v.engine || null, type: v.type || null, added: v.added || Date.now() }; }
  function garageLoad() {
    var g = null;
    try { g = JSON.parse(localStorage.getItem(GARAGE_KEY) || 'null'); } catch (e) { g = null; }
    if (!g || !Array.isArray(g.vehicles)) {
      var cur = null; try { cur = JSON.parse(localStorage.getItem('dna_vehicle') || 'null'); } catch (e) { cur = null; }
      g = { vehicles: cur && cur.make ? [stripVehicle(cur)] : [], active: cur && cur.make ? vehicleKey(cur) : null };
    }
    g.vehicles = g.vehicles.filter(function (v) { return v && v.make; });
    if (!g.vehicles.some(function (v) { return vehicleKey(v) === g.active; })) g.active = g.vehicles.length ? vehicleKey(g.vehicles[0]) : null;
    return g;
  }
  function garageActive(g) { g = g || garageLoad(); return g.vehicles.filter(function (v) { return vehicleKey(v) === g.active; })[0] || null; }
  function garageWrite(g, opts) {
    opts = opts || {};
    try { localStorage.setItem(GARAGE_KEY, JSON.stringify(g)); } catch (e) { /* ignore */ }
    var a = garageActive(g);
    try { if (a) localStorage.setItem('dna_vehicle', JSON.stringify(a)); else localStorage.removeItem('dna_vehicle'); } catch (e) { /* ignore */ }
    if (!opts.quiet) { document.querySelectorAll('.dna-fitbadge').forEach(function (b) { b.remove(); }); try { badgeCards(); productFitCheck(); renderGarage(); } catch (e) { /* ignore */ } }
    if (!opts.local) garageSync(g);
  }
  function garageAdd(v) {
    if (!v || !v.make) return;
    var g = garageLoad(), nv = stripVehicle(v), key = vehicleKey(nv);
    var same = g.vehicles.filter(function (x) { return vehicleKey(x) === key; })[0];
    if (!same) {
      // the same year/make/model saved without cab or engine → refine it instead of adding a twin
      var loose = g.vehicles.filter(function (x) { return x.make === nv.make && (x.model || '') === (nv.model || '') && (x.year || '') === (nv.year || '') && !x.body && !x.engine; })[0];
      if (loose) { g.vehicles.splice(g.vehicles.indexOf(loose), 1); }
      g.vehicles.unshift(nv);
      if (g.vehicles.length > 8) g.vehicles.length = 8;
    }
    g.active = key;
    garageWrite(g);
  }
  function garageSelect(key) { var g = garageLoad(); if (g.vehicles.some(function (x) { return vehicleKey(x) === key; })) { g.active = key; garageWrite(g); } }
  function garageRemove(key) { var g = garageLoad(); g.vehicles = g.vehicles.filter(function (x) { return vehicleKey(x) !== key; }); if (g.active === key) g.active = g.vehicles.length ? vehicleKey(g.vehicles[0]) : null; garageWrite(g); }
  // ---- server copy for signed-in customers (Shopify app proxy → alex-ops /garage; off until the proxy exists)
  function garageSyncOn() { var s = document.querySelector('[data-garage-sync="true"][data-customer]'); return s && s.getAttribute('data-customer') ? s : null; }
  var syncTimer = null;
  function garageSync(g) {
    var s = garageSyncOn(); if (!s) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      fetch('/apps/garage', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vehicles: g.vehicles, active: g.active }) }).catch(function () { /* offline / proxy off */ });
    }, 400);
  }
  var pulled = false;
  function garagePull() {
    var s = garageSyncOn(); if (!s || pulled) return; pulled = true;
    fetch('/apps/garage', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (remote) {
        if (!remote || !Array.isArray(remote.vehicles)) return;
        var g = garageLoad(), keys = {}, changed = false;
        g.vehicles.forEach(function (v) { keys[vehicleKey(v)] = true; });
        remote.vehicles.forEach(function (v) { if (v && v.make && !keys[vehicleKey(v)]) { g.vehicles.push(stripVehicle(v)); keys[vehicleKey(v)] = true; changed = true; } });
        if (!g.active && remote.active) { g.active = remote.active; changed = true; }
        if (changed) garageWrite(g, { local: g.vehicles.length === remote.vehicles.length });
      })
      .catch(function () { /* ignore */ });
  }

  // ---- rendering (sections/dna-garage.liquid: mode "home" = rows per category, mode "full" = the Shop-for-my-vehicle page)
  var garageReq = 0;
  function fitTag(v) { return 'fit-' + (v.make + ' ' + (v.model || '')).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function garageTile(it, fit) {
    var off = it.cp && it.cp > it.p ? Math.round((1 - it.p / it.cp) * 100) : 0;
    return '<div class="dna-va__card dna-gar__card">' +
      '<div class="dna-va__tile"><a class="dna-va__media" href="/products/' + esc(it.h) + '">' + (it.img ? '<img src="' + esc(it.img) + '" alt="" loading="lazy">' : '') + (off ? '<span class="dna-va__off">' + off + '% off</span>' : '') + '</a>' +
      (it.av && it.vid ? '<button type="button" class="dna-va__add" data-add="' + esc(it.vid) + '" data-multi="' + (it.nv > 1 ? 1 : 0) + '" data-handle="' + esc(it.h) + '" aria-label="Add to cart">+</button>' : '') + '</div>' +
      priceLine(it) +
      '<a class="dna-va__title" href="/products/' + esc(it.h) + '">' + esc(it.t) + '</a>' +
      (fit && fit.label ? '<div class="dna-va__fit dna-va__fit--' + fit.tone + '">' + esc(fit.label) + '</div>' : '') +
      (it.av ? '' : '<div class="dna-va__soldout">Sold out</div>') + '</div>';
  }
  function fetchGarage(cats, tag, all) {
    var out = {}, i = 0;
    function get(url) { return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }); }
    function next() {
      if (i >= cats.length) return Promise.resolve();
      var c = cats[i++], base = '/collections/' + encodeURIComponent(c.h) + '/' + encodeURIComponent(tag) + '?view=dna-fit';
      return get(base).then(function (j) {
        if (!j || !all || !(j.pages > 1)) return j;
        var extra = []; for (var p = 2; p <= Math.min(j.pages, 6); p++) extra.push(get(base + '&page=' + p));
        return Promise.all(extra).then(function (more) { more.forEach(function (m) { if (m && m.items) j.items = j.items.concat(m.items); }); j.pages = 1; return j; });
      }).then(function (j) { out[c.h] = j; }, function () { out[c.h] = null; }).then(next);
    }
    return Promise.all([next(), next(), next(), next(), next(), next()]).then(function () { return out; });
  }
  function garagePicker(g, full) {
    var html = '<div class="dna-gar__picker" role="tablist">';
    g.vehicles.forEach(function (v) {
      var k = vehicleKey(v), on = k === g.active;
      html += '<button type="button" class="dna-gar__chip' + (on ? ' is-active' : '') + '" data-garage-select="' + esc(k) + '" role="tab" aria-selected="' + on + '">' + esc(shortName(v)) + '</button>';
    });
    html += '<button type="button" class="dna-gar__chip dna-gar__chip--add" data-garage-add>+ Add vehicle</button></div>';
    return html;
  }
  function renderGarage() {
    var boxes = document.querySelectorAll('.dna-gar[data-mode]'); if (!boxes.length) return;
    garagePull();
    Array.prototype.forEach.call(boxes, function (box) { renderGarageBox(box); });
  }
  function renderGarageBox(box) {
    var head = box.querySelector('.dna-gar__head'), body = box.querySelector('.dna-gar__body'); if (!head || !body) return;
    var full = box.getAttribute('data-mode') === 'full', pageUrl = box.getAttribute('data-page-url') || '/pages/shop-for-my-vehicle';
    if (!box.dnaWired) {
      box.dnaWired = true;
      box.addEventListener('click', function (ev) {
        var add = ev.target.closest('[data-add]'); if (add) { ev.preventDefault(); quickAdd(add); return; }
        var sel = ev.target.closest('[data-garage-select]'); if (sel) { ev.preventDefault(); garageSelect(sel.getAttribute('data-garage-select')); return; }
        var rm = ev.target.closest('[data-garage-remove]'); if (rm) { ev.preventDefault(); if (window.confirm('Remove this vehicle from your garage?')) garageRemove(rm.getAttribute('data-garage-remove')); return; }
        var ad = ev.target.closest('[data-garage-add]'); if (ad) { ev.preventDefault(); open({ fresh: true }); return; }
        var sort = ev.target.closest('[data-garage-sort]'); if (sort) { ev.preventDefault(); box.setAttribute('data-sort', sort.getAttribute('data-garage-sort')); try { localStorage.setItem('dna_garage_sort', sort.getAttribute('data-garage-sort')); } catch (e) { /* ignore */ } renderGarageBox(box); return; }
      });
      if (full) { try { box.setAttribute('data-sort', localStorage.getItem('dna_garage_sort') || 'category'); } catch (e) { box.setAttribute('data-sort', 'category'); } }
    }
    var g = garageLoad(), v = garageActive(g);
    box.hidden = false;
    var heading = box.getAttribute('data-heading') || (full ? 'Shop for my vehicle' : 'Parts for your');
    if (!v) {
      box.classList.add('dna-gar--empty');
      head.innerHTML = (full ? '<h1 class="dna-gar__h1">' + esc(heading) + '</h1>' : '') +
        '<div class="dna-gar__prompt"><div class="dna-gar__prompt-text"><strong>' + (full ? 'Add your first vehicle' : 'Shop parts for your vehicle') + '</strong><span>Tell us what you drive and we\'ll show only the parts that fit. Save more than one and switch any time.</span></div>' +
        '<button type="button" class="dna-gar__btn" data-garage-add>' + (supported ? '🎙 ' : '') + 'Add my vehicle</button></div>';
      body.innerHTML = '';
      return;
    }
    box.classList.remove('dna-gar--empty');
    var tag = fitTag(v), req = ++garageReq;
    var meta = [v.body, v.engine].filter(Boolean).join(' · ');
    head.innerHTML = (full ? '<h1 class="dna-gar__h1">' + esc(heading) + '</h1>' : '') + garagePicker(g, full) +
      '<div class="dna-gar__titlerow"><div class="dna-gar__title">' + (full ? '' : '<span class="dna-gar__eyebrow">Your garage</span>') +
      '<h2>' + (full ? esc(fullName(v)) : esc(heading) + ' ' + esc(shortName(v))) + '</h2><p class="dna-gar__count">' + esc(meta || 'Finding parts…') + '</p></div>' +
      '<div class="dna-gar__actions">' + (full ? '<button type="button" class="dna-gar__change" data-garage-remove="' + esc(vehicleKey(v)) + '">Remove</button>' : '<a class="dna-gar__change dna-gar__change--go" href="' + esc(pageUrl) + '">Shop all →</a>') + '</div></div>' +
      (full ? '<div class="dna-gar__sort" role="tablist"><span>Sort by</span><button type="button" class="dna-gar__sortbtn" data-garage-sort="category">Category</button><button type="button" class="dna-gar__sortbtn" data-garage-sort="type">Part type</button><button type="button" class="dna-gar__sortbtn" data-garage-sort="price">Price</button></div>' : '');
    var cur = box.getAttribute('data-sort') || 'category';
    Array.prototype.forEach.call(head.querySelectorAll('[data-garage-sort]'), function (b) { b.classList.toggle('is-active', b.getAttribute('data-garage-sort') === cur); });
    body.innerHTML = '<div class="dna-gar__loading">Finding parts for your ' + esc(badgeName(v)) + '…</div>';
    var cats; try { cats = JSON.parse(box.getAttribute('data-categories') || '[]'); } catch (e) { cats = []; }
    var key = 'dna_garage:' + tag + (full ? ':full' : ''), cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (e) { cached = null; }
    var ready = cached && cached.data && Date.now() - cached.at < 1800000 ? Promise.resolve(cached.data)
      : fetchGarage(cats, tag, full).then(function (data) { try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data: data })); } catch (e) { /* quota */ } return data; });
    ready.then(function (data) { if (req === garageReq) paintGarage(box, head, body, cats, data, v, tag, full); })
      .catch(function () { if (req === garageReq) body.innerHTML = '<div class="dna-gar__none">Couldn\'t load parts right now — pull to refresh.</div>'; });
  }
  function paintGarage(box, head, body, cats, data, v, tag, full) {
    var groups = [], seen = {}, total = 0, truncated = false;
    cats.forEach(function (c) {
      var j = data[c.h]; if (!j || !j.items || !j.items.length) return;
      var rows = j.items.map(function (it) { return { it: it, fit: fitStructured(it.fit, v), cat: c }; })
        .filter(function (r) { return r.fit && (r.fit.tone === 'fit' || r.fit.tone === 'maybe'); });
      if (full) rows = rows.filter(function (r) { if (seen[r.it.h]) return false; seen[r.it.h] = true; return true; });
      if (!rows.length) return;
      rows.sort(function (a, b) { return (b.fit.score - a.fit.score) || ((b.it.av ? 1 : 0) - (a.it.av ? 1 : 0)); });
      if (j.pages > 1) truncated = true;
      total += rows.length;
      groups.push({ key: c.h, title: c.t, url: '/collections/' + c.h + '/' + tag, rows: rows, more: j.pages > 1 });
    });
    var count = head.querySelector('.dna-gar__count'), meta = [v.body, v.engine].filter(Boolean).join(' · ');
    if (!groups.length) {
      if (count) count.textContent = (meta ? meta + ' · ' : '') + 'nothing listed yet';
      body.innerHTML = '<div class="dna-gar__none">We don\'t have parts listed for your ' + esc(badgeName(v)) + ' yet. <a href="/pages/contact?topic=source&vehicle=' + encodeURIComponent(fullName(v)) + '">Ask our parts team</a> — we can usually source it in a day or two.</div>';
      return;
    }
    var html = '';
    if (!full) {
      if (count) count.textContent = 'Parts that fit, in ' + groups.length + ' categor' + (groups.length === 1 ? 'y' : 'ies') + (meta ? ' · ' + meta : '');
      groups.forEach(function (gr) {
        html += '<section class="dna-gar__cat"><div class="dna-gar__cathead"><h3>' + esc(gr.title) + '</h3><a href="' + esc(gr.url) + '">See all</a></div><div class="dna-gar__row">';
        gr.rows.slice(0, 10).forEach(function (r) { html += garageTile(r.it, r.fit); });
        if (gr.rows.length > 10 || gr.more) html += '<a class="dna-gar__moretile" href="' + esc(gr.url) + '"><span>→</span>See all ' + esc(gr.title.toLowerCase()) + '</a>';
        html += '</div></section>';
      });
      body.innerHTML = html;
      return;
    }
    // full page: every fitting part, grouped by category, by part type, or one price-sorted grid
    var sort = box.getAttribute('data-sort') || 'category';
    if (count) count.textContent = 'Every part that fits' + (meta ? ' · ' + meta : '');
    var sections = [];
    if (sort === 'type') {
      var byType = {};
      groups.forEach(function (gr) { gr.rows.forEach(function (r) { var t = (r.it.ty || gr.title).replace(/^(Automotive|Vehicle|Car|Truck)\s+/i, ''); (byType[t] = byType[t] || []).push(r); }); });
      Object.keys(byType).sort(function (a, b) { return byType[b].length - byType[a].length || a.localeCompare(b); }).forEach(function (t) { sections.push({ title: t, rows: byType[t] }); });
    } else if (sort === 'price') {
      var all = []; groups.forEach(function (gr) { all = all.concat(gr.rows); });
      all.sort(function (a, b) { return (a.it.p - b.it.p) || (b.fit.score - a.fit.score); });
      sections.push({ title: 'All parts, lowest price first', rows: all });
    } else {
      groups.forEach(function (gr) { sections.push({ title: gr.title, rows: gr.rows, url: gr.url }); });
    }
    html += '<nav class="dna-gar__jump">' + sections.map(function (s, i) { return '<a href="#dna-gar-s' + i + '">' + esc(s.title) + ' <b>' + s.rows.length + '</b></a>'; }).join('') + '</nav>';
    sections.forEach(function (s, i) {
      html += '<section class="dna-gar__group" id="dna-gar-s' + i + '"><div class="dna-gar__cathead"><h3>' + esc(s.title) + ' <span>' + s.rows.length + '</span></h3>' + (s.url ? '<a href="' + esc(s.url) + '">Open category</a>' : '') + '</div><div class="dna-gar__grid">';
      s.rows.forEach(function (r) { html += garageTile(r.it, r.fit); });
      html += '</div></section>';
    });
    body.innerHTML = html;
  }

  // ======================================================================= wiring
  function looksLikeVehicleQuery(q) { var v = vehicleFrom(q); return !!(v && v.make && (v.model || v.year)); }
  function wire() {
    ensureSheet();
    document.querySelectorAll('[data-dna-mic]').forEach(function (b) {
      if (!supported) { b.classList.add('is-unsupported'); b.setAttribute('aria-label', 'Ask the assistant'); }
      b.addEventListener('click', function (ev) { ev.preventDefault(); open(supported ? { listen: true } : {}); });
    });
    document.querySelectorAll('.dna-msearch__form').forEach(function (f) {
      f.addEventListener('submit', function (ev) {
        var q = (f.querySelector('input[name="q"]') || {}).value || '';
        if (looksLikeVehicleQuery(q)) { ev.preventDefault(); open({ text: q }); }
      });
    });
    document.addEventListener('click', function (ev) {
      var a = ev.target.closest && ev.target.closest('[data-dna-assist]');
      if (a && !a.closest('#dna-account')) { ev.preventDefault(); open({}); }
      var c = ev.target.closest && ev.target.closest('[data-dna-fit-change]');
      if (c) { ev.preventDefault(); open({ fresh: true }); }
    });
    if (/[?&]assist=1\b/.test(location.search)) open({});
    badgeCards(); productFitCheck(); renderGarage();
    new MutationObserver(function () { badgeCards(); }).observe(document.body, { childList: true, subtree: true });
  }
  function onNavigate() { try { badgeCards(); productFitCheck(); renderGarage(); } catch (e) { /* ignore */ } }
  // ---- "Fits your truck" on product cards and the product page, from the saved vehicle + the dna.fitment metafield
  function badgeCards() {
    var v = loadVehicle(); if (!v || !v.make) return;
    document.querySelectorAll('.card-wrapper[data-dna-fit]').forEach(function (el) {
      if (el.querySelector('.dna-fitbadge')) return;
      var fit; try { fit = JSON.parse(el.getAttribute('data-dna-fit') || 'null'); } catch (e) { fit = null; }
      var r = fitStructured(fit, v); if (!r) return;
      if (r.tone !== 'fit' && r.tone !== 'no') return;               // only a clear yes / no on tiles
      var host = el.querySelector('.card-information') || el.querySelector('.card__content') || el;
      var b = document.createElement('span'); b.className = 'dna-fitbadge dna-fitbadge--' + r.tone;
      b.textContent = r.tone === 'fit' ? '✓ ' + r.label : '✗ Not for your ' + badgeName(v);
      host.appendChild(b);
    });
  }
  function productFitCheck() {
    var box = document.querySelector('.dna-fit'); if (!box) return;
    var v = loadVehicle(); var out = box.querySelector('.dna-fit__check'); if (!out) return;
    var fit; try { fit = JSON.parse(box.getAttribute('data-dna-fit') || 'null'); } catch (e) { fit = null; }
    if (!v || !v.make) {
      out.innerHTML = '<button type="button" class="dna-fit__btn" data-dna-assist>Does it fit my vehicle? Tell us your vehicle</button>';
      out.hidden = false; return;
    }
    var r = fitStructured(fit, v);
    var name = esc(badgeName(v));
    if (!r) out.innerHTML = '<span class="dna-fit__pill dna-fit__pill--none">No fitment listed — <a href="/pages/contact?topic=fitment&product=' + encodeURIComponent(document.title.split(' – ')[0].split(' | ')[0]) + '">ask us about your ' + name + '</a></span>';
    else if (r.tone === 'fit') out.innerHTML = '<span class="dna-fit__pill dna-fit__pill--fit">✓ ' + esc(r.label) + '</span>';
    else if (r.tone === 'maybe') out.innerHTML = '<span class="dna-fit__pill dna-fit__pill--maybe">' + esc(r.label) + ' — <a href="/pages/contact?topic=fitment&product=' + encodeURIComponent(document.title.split(' – ')[0].split(' | ')[0]) + '">ask us</a></span>';
    else out.innerHTML = '<span class="dna-fit__pill dna-fit__pill--no">✗ Not listed for your ' + name + ' — <a href="/pages/contact?topic=source&part=' + encodeURIComponent(document.title.split(' – ')[0].split(' | ')[0]) + '&vehicle=' + encodeURIComponent(fullName(v)) + '">ask us to find one</a></span>';
    out.hidden = false;
    var change = box.querySelector('[data-dna-fit-change]'); if (change) { change.hidden = false; change.textContent = 'Not your ' + badgeName(v) + '? Change'; }
  }
  window.dnaAssistant = { open: open, close: close, parse: function (t) { var v = vehicleFrom(t); return { vehicle: v, parts: partsFrom(t, v) }; }, fitment: fitment, fitStructured: fitStructured, badgeCards: badgeCards, onNavigate: onNavigate, renderGarage: renderGarage, garage: { list: garageLoad, active: garageActive, add: garageAdd, select: garageSelect, remove: garageRemove }, supported: supported };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire); else wire();
})();
